import React, { useEffect, useRef, useState, useCallback } from "react";
import { ConvergeCell, MarginCell, ReturnCell, WorkbookNote } from "@/components/FuturesScenario";
import { futuresNet } from "@/lib/netPnl";
import { ANALYSE_LABEL, CardDialog, CardFooter, CardSections, CompactSummary, ModePill, NetPnlPair, PnlFigure, Section as KitSection, SQUARE_OFF_LABEL, TradeCard as CardFrame, CardScale, DetailsButton, TradeDetailsModal } from "@/components/trade/TradeCardShell";
import { futuresRow } from "@/lib/commonTrades";
import { progressText } from "@/lib/marks";
import { FuturesSpreadAnalysis } from "@/components/trade/TradeAnalysis";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { detailText, refusalText } from "@/lib/api";
import TerminalDrawer from "../components/TerminalDrawer";
import RequestStatus from "../components/RequestStatus";
import { inr, num } from "@/lib/format";
import { targetRupees, targetPercent } from "@/lib/targetValues";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import DisabledReasonButton from "../components/DisabledReasonButton";
import RolloverStatus, { rolloverStatus, rolloverWaitReason } from "../components/trade/RolloverStatus";
import {
  Zap, AlertTriangle, Play, Square, XCircle, RotateCcw, Plus,
  ShieldCheck, ShieldAlert, ClipboardList,
  Crosshair, CheckCircle2, ChevronsRight, Maximize2, ListChecks, Settings, Trash2, History, Info, BarChart3,
} from "lucide-react";
import { sharedGet, visibleInterval } from "@/lib/poll";
import { useApp } from "@/context/AppContext";
import { marketWait } from "@/lib/statusModel";

const LEVEL_BADGE = {
  ENTERING: "bg-[#FFCC00]/15 text-[#FFCC00]", RETRYING: "bg-[#FFCC00]/15 text-[#FFCC00]",
  STALLED: "bg-[#FF3B30]/15 text-[#FF6b62]",
  WAITING: "bg-[#475569]/20 text-slate-500", NEXT: "bg-[#FFCC00]/15 text-[#FFCC00]",
  OPEN: "bg-[#00C805]/15 text-[#00C805]", CLOSED: "bg-[#7c3aed]/20 text-[#c4b5fd]",
  UNFUNDED: "bg-[#FF3B30]/15 text-[#FF6b62]",
};
const MARGIN_SOURCE_LABEL = {
  ZERODHA: "ZERODHA PAIR ECONOMICS", MANUAL_OVERRIDE: "MANUAL PAIR ECONOMICS",
  BROKER: "BROKER", BROKER_SCALED: "BROKER (scaled)", ENGINE_ESTIMATE: "ENGINE ESTIMATE",
  UNAVAILABLE: "UNAVAILABLE",
};
const MARGIN_SOURCE_CLS = {
  ZERODHA: "bg-[#00C805]/15 text-[#00C805]", MANUAL_OVERRIDE: "bg-[#5b9bff]/15 text-[#5b9bff]",
  BROKER: "bg-[#00C805]/15 text-[#00C805]", BROKER_SCALED: "bg-[#00C805]/15 text-[#00C805]",
  ENGINE_ESTIMATE: "bg-[#FFCC00]/15 text-[#FFCC00]", UNAVAILABLE: "bg-[#475569]/20 text-slate-500",
};
// evaluate_entry() reason codes -> human labels (display translation only, values unchanged)
const REASON_LABELS = {
  TRADE_STOPPED: "Trade not running",
  NO_OPPORTUNITY_ASSIGNED: "No opportunity assigned",
  RESET_ACTIVE: "Reset in progress",
  LEVEL_ALREADY_ENTERED: "Level already entered",
  EDGE_BELOW_THRESHOLD: "Edge below K1 threshold",
  PRIOR_LEVEL_NOT_FILLED: "Previous level not yet filled",
  SPREAD_BELOW_LEVEL_THRESHOLD: "Spread trigger not reached",
  FUNDS_UNAVAILABLE: "Insufficient funds",
  SPREAD_BELOW_TRIGGER: "Spread below the level's trigger — level paused",
};

/** Zerodha's own available funds (display only) — never the trade's allocation. */
function brokerFundsText(bf) {
  if (!bf || bf.available == null) return "Not connected";
  return inr(bf.available);
}
function brokerFundsSub(bf) {
  if (!bf || bf.available == null) return "no live Zerodha funds for this trade's owner";
  const basis = bf.basis === "SINGLE_LEDGER" ? "single ledger" : `${(bf.segment || "").toLowerCase()} segment`;
  const age = bf.feed_age_seconds != null ? ` · ${Math.round(bf.feed_age_seconds)}s ago` : "";
  return `${bf.broker_user_id ? bf.broker_user_id + " · " : ""}${basis}${age}${bf.feed_stale ? " · STALE" : ""}`;
}

/** The ENTERING level's retry / stalled state, from the backend's pair progress (one source). */
export function entryBlock(trade) {
  return trade?.pair_progress?.in_flight?.entry_block || null;
}

function EntryBlockBanner({ block, testId }) {
  if (!block) return null;
  const stalled = block.state === "STALLED";
  return (
    <div data-testid={testId}
      className={`rounded-sm border px-2.5 py-1.5 text-[11px] mb-3 ${stalled
        ? "border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF6b62]"
        : "border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00]"}`}>
      {stalled ? "⚠ STALLED — " : "↻ RETRY PENDING — "}{block.text}
    </div>
  );
}

function Field({ label, value, cls }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xs mono ${cls || "text-slate-200"}`}>{value}</div>
    </div>
  );
}

/** Status label for one ladder row, derived purely from already-provided fields
 * (level_status + comparison against next_level_number/capacity_affordable) — no strategy
 * logic here. UNFUNDED marks a not-yet-entered level beyond what allocated capital can
 * reach (capacity_affordable === false), per the margin-capacity check — distinct from
 * WAITING, which just means "not reached yet but affordable when we get there". */
function ladderStatus(row, nextLevelNumber, block) {
  if (row.level_status === "ENTERING") {
    if (block?.state === "STALLED") return "STALLED";
    if (block?.state === "RETRY_PENDING") return "RETRYING";
    return "ENTERING";
  }
  if (row.level_status === "CLOSED") return "CLOSED";
  if (row.level_status === "OPEN") return "OPEN";
  if (!row.funding_status && row.capacity_affordable === false) return "UNFUNDED";
  if (row.level_number === nextLevelNumber) return "NEXT";
  return "WAITING";
}

/** Full 10-level averaging plan table (backend-computed ladder_plan, merged with any fill). */
function LadderTable({ ladderPlan, nextLevelNumber, block }) {
  if (!ladderPlan || ladderPlan.length === 0) {
    return <div className="text-xs text-slate-600 py-3 text-center border border-[#2A2F3A] rounded-sm mt-2">Assign an opportunity to see the full averaging plan.</div>;
  }
  const regressed = ladderPlan.filter((l) => l.entry_spread_regressed);
  const pairMode = ladderPlan.some(l => l.funding_status != null);
  return (
    <>
    {regressed.length > 0 && (
      <div className="mt-2 rounded-sm border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-2.5 py-1.5 text-[11px] text-[#FF6b62]"
        data-testid="entry-spread-regression-warning">
        ⚠ ENTRY SPREAD DID NOT WIDEN VS PREVIOUS LEVEL — {regressed.map((l) => `L${l.level_number}`).join(", ")}.
        The workbook latches the LTP the trigger fired on, so an executed spread at or below the previous level means the fill diverged.
      </div>
    )}
    <div className="terminal-table-scroll rounded-sm border border-[#2A2F3A] mt-2" tabIndex={0} role="region" aria-label="Averaging plan — scroll horizontally for all columns">
      <table className="w-full text-[11px] border-collapse mono">
        <caption className="sr-only">Averaging plan: execution progress and capital affordability are separate.</caption>
        <thead className="bg-[#1A1D24] text-slate-400">
          <tr>{["Lvl", "Execution Status", "Trig %", "Trig Spread", "SELL fill", "BUY fill", "Actual Spread", "Δ vs Trigger", "Δ vs Prev", "Required Lots", "Affordable Lots", "Executed Lots", "Funding Status", "Planned Average Spread", "Qty",
                ...(pairMode ? [] : ["Broker Exec Margin", "Broker Hedged Margin", "Margin Source"]), "Reason"].map((h) => (
            <th key={h} className="px-2 py-1.5 text-right first:text-left whitespace-nowrap font-semibold sticky top-0 bg-[#1A1D24] first:left-0 first:z-20 z-10">{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {ladderPlan.map((l) => {
            const status = ladderStatus(l, nextLevelNumber, block);
            const unfunded = status === "UNFUNDED";
            return (
              <tr key={l.level_number}
                className={`gz-row ${status === "NEXT" ? "bg-[#FFCC00]/[0.06]" : ""} ${unfunded ? "opacity-60" : ""}`}
                data-testid={`ladder-row-${l.level_number}`}>
                <td className="px-2 py-1.5 text-left font-bold text-slate-100 sticky left-0 bg-[#12151C] z-10">L{l.level_number}</td>
                <td className="px-2 py-1.5 text-right">
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${LEVEL_BADGE[status]}`}>{status}</span>
                </td>
                <td className="px-2 py-1.5 text-right">{num(l.trigger_pct, 2)}%</td>
                <td className="px-2 py-1.5 text-right" data-testid={`ladder-trigger-${l.level_number}`}
                  title={l.rolled ? `Rollover replacement fills (set ${l.rolled_into_set}) — this level never traded on the re-based trigger`
                    : l.trigger_source === "LATCHED" && l.ladder_trigger_spread != null && Math.abs(l.ladder_trigger_spread - l.trigger_spread) > 1e-9
                      ? `Trigger this level latched at. The ladder is now anchored on ${num(l.ladder_trigger_spread, 4)} (L1's fill).` : undefined}>
                  {l.rolled ? <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-[#5b9bff]/15 text-[#5b9bff]" data-testid={`ladder-rolled-${l.level_number}`}>ROLLED</span>
                    : <>{num(l.trigger_spread, 4)}{l.trigger_source === "LATCHED" && l.ladder_trigger_spread != null && Math.abs(l.ladder_trigger_spread - l.trigger_spread) > 1e-9
                        && <span className="block text-[9px] text-slate-500">anchor {num(l.ladder_trigger_spread, 4)}</span>}</>}</td>
                {/* Trigger-vs-fill transparency. In the workbook the latched Entry Limit
                    Price IS the LTP the trigger fired on, so Actual Spread should equal the
                    spread that triggered and Δ vs Prev should stay positive. */}
                <td className="px-2 py-1.5 text-right text-[#FF6b62]">{l.sell_entry_fill != null ? num(l.sell_entry_fill, 2) : "—"}</td>
                <td className="px-2 py-1.5 text-right text-[#5b9bff]">{l.buy_entry_fill != null ? num(l.buy_entry_fill, 2) : "—"}</td>
                <td className={`px-2 py-1.5 text-right font-semibold ${l.entry_spread_regressed ? "text-[#FF6b62]" : "text-slate-100"}`}
                  title={l.entry_spread_regressed ? "ENTRY SPREAD DID NOT WIDEN VS PREVIOUS LEVEL" : undefined}>
                  {l.entry_spread != null ? num(l.entry_spread, 4) : "—"}
                  {l.entry_spread_regressed && <span className="ml-1 text-[#FF6b62]">⚠</span>}
                </td>
                <td className={`px-2 py-1.5 text-right ${l.slippage_vs_trigger != null && l.slippage_vs_trigger < 0 ? "text-[#FF6b62]" : "text-slate-400"}`}>
                  {l.rolled ? "n/a" : l.slippage_vs_trigger != null ? num(l.slippage_vs_trigger, 4) : "—"}</td>
                <td className={`px-2 py-1.5 text-right ${l.change_vs_previous != null && l.change_vs_previous <= 0 ? "text-[#FF6b62]" : "text-[#00C805]"}`}>
                  {l.rolled ? "n/a" : l.change_vs_previous != null ? num(l.change_vs_previous, 4) : "—"}</td>
                <td className="px-2 py-1.5 text-right">{l.required_lots}</td>
                <td className="px-2 py-1.5 text-right">{l.affordable_lots ?? "—"}</td>
                <td className="px-2 py-1.5 text-right">{l.executed_lots ?? "—"}</td>
                <td className="px-2 py-1.5 text-right whitespace-nowrap">{l.funding_status ?? (l.fundable ? "FULLY AFFORDABLE" : "NOT AFFORDABLE")}</td>
                <td className="px-2 py-1.5 text-right">{num(l.planned_average_spread, 4)}</td>
                <td className="px-2 py-1.5 text-right">{num(l.required_qty, 0)}</td>
                {!pairMode && <>
                  <td className="px-2 py-1.5 text-right">{inr(l.execution_funds_required)}</td>
                  <td className="px-2 py-1.5 text-right">{inr(l.hedged_margin_required)}</td>
                  <td className="px-2 py-1.5 text-right">{l.margin_source}</td>
                </>}
                <td className="px-2 py-1.5 text-right text-slate-500 whitespace-nowrap">{l.rolled ? "ROLLED"
                  : status === "STALLED" ? `stalled at pair ${block.pair}`
                  : status === "RETRYING" ? `retry ${block.retries_used + 1}/${block.max_retries} pending`
                  : l.entry_spread_regressed ? "DID NOT WIDEN" : (l.exit_reason || (unfunded ? "UNFUNDED" : "—"))}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    </>
  );
}

/** Compact "L1 ✓ → L2 ✓ → L3 NEXT → L4 → …" chain + lots sequence, built entirely from
 * already-provided ladder_plan fields. */
function AveragingPlanSummary({ ladderPlan, nextLevelNumber, enteredLevels }) {
  if (!ladderPlan || ladderPlan.length === 0) return null;
  return (
    <div className="term-panel-2 rounded-sm p-2.5 mb-2" data-testid="averaging-plan-summary">
      <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Averaging Plan</div>
      <div className="flex items-center flex-wrap gap-1 text-[11px] mono mb-1.5">
        {ladderPlan.map((l, i) => {
          const status = ladderStatus(l, nextLevelNumber);
          return (
            <React.Fragment key={l.level_number}>
              {i > 0 && <ChevronsRight className="h-3 w-3 text-slate-600 shrink-0" />}
              <span className={`px-1.5 py-0.5 rounded-sm font-bold flex items-center gap-1 ${LEVEL_BADGE[status]}`}>
                L{l.level_number}
                {(status === "OPEN" || status === "CLOSED") && <CheckCircle2 className="h-3 w-3" />}
                {status === "NEXT" && "NEXT"}
                {status === "UNFUNDED" && "UNFUNDED"}
              </span>
            </React.Fragment>
          );
        })}
      </div>
      <div className="text-[11px] text-slate-400">
        <span className="font-semibold text-slate-200">{enteredLevels} / {ladderPlan.length}</span> Levels Executed
        <span className="mx-2 text-slate-700">·</span>
        Lots: <span className="mono text-slate-300">{ladderPlan.map((l) => l.required_lots).join(" → ")}</span>
      </div>
    </div>
  );
}

function CheckRow({ ok, label, skip }) {
  if (skip) return null;
  return (
    <div className="flex items-center gap-1.5 text-[11px]">
      {ok ? <CheckCircle2 className="h-3.5 w-3.5 text-[#00C805] shrink-0" /> : <XCircle className="h-3.5 w-3.5 text-[#FF6b62] shrink-0" />}
      <span className={ok ? "text-slate-300" : "text-[#FF6b62]"}>{label}</span>
    </div>
  );
}

/** TAB 1 — NEXT EXECUTION. No value here is computed in the frontend beyond simple display
 * arithmetic (e.g. formatting); every number originates from `next_level_detail`. */
function NextExecutionTab({ trade }) {
  const nd = trade.next_level_detail;
  // GATE A — the authoritative next-pair decision, identical in PAPER, SHADOW LIVE and LIVE.
  // Never the legacy whole-level figure, which is not what admits the next pair.
  const npa = nd?.next_pair_affordability;
  if (!trade.assigned_commodity) {
    return <div className="text-xs text-slate-500 p-3">Assign an opportunity to see the next execution preview.</div>;
  }
  if (!nd) {
    return <div className="text-xs text-[#00C805] p-3">All averaging levels have been entered — averaging plan complete.</div>;
  }
  const checks = nd.checks || {};
  const readableReasons = (nd.reasons || []).map((r) => REASON_LABELS[r] || r);
  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-1.5 mb-3">
        <span className="text-xs font-bold text-slate-100 flex items-center gap-1.5">
          <Crosshair className="h-3.5 w-3.5 text-[#2563EB]" /> {nd.is_entering ? "CURRENT LEVEL" : "NEXT EXECUTION"} — L{nd.level_number}
        </span>
        <div className="flex items-center gap-1.5">
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${MARGIN_SOURCE_CLS[nd.margin_source] || MARGIN_SOURCE_CLS.UNAVAILABLE}`}>
            {MARGIN_SOURCE_LABEL[nd.margin_source] || nd.margin_source}{nd.margin_stale ? " (stale)" : ""}
          </span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm ${nd.eligible ? "bg-[#00C805]/15 text-[#00C805]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
            {nd.eligible ? "READY" : "WAITING"}
          </span>
        </div>
      </div>

      <EntryBlockBanner block={nd.is_entering ? entryBlock(trade) : null} testId={`entry-block-${trade.slot}`} />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Field label="Current / Running Spread" value={num(trade.current_spread, 4)} />
        <Field label="Progress" value={`${nd.pairs_complete} / ${nd.pairs_required}`} />
        <Field label="Next Pair (in level)"
               value={(() => { const b = nd.is_entering ? entryBlock(trade) : null;
                 if (b?.state === "STALLED") return `— stalled at pair ${b.pair}`;
                 if (b?.state === "RETRY_PENDING") return `${b.pair} · retry ${b.retries_used + 1}/${b.max_retries}`;
                 return nd.next_pair ?? "—"; })()} />
        <Field label="Gate A" value={nd.gate_a_state ?? "—"} />
        <Field label="Last Executed Spread" value={trade.last_executed ? num(trade.last_executed.entry_spread, 4) : "—"} />
        <Field label="Next Trigger Spread"
               value={nd.spread_gate_applies === false ? "n/a at L1"
                      : `${num(nd.trigger_spread, 4)}${nd.is_entering ? " (latched)" : ""}`}
               cls={nd.spread_gate_applies === false ? "text-slate-600" : undefined} />
        <Field label="Distance to Trigger"
               value={nd.spread_gate_applies === false ? "n/a at L1"
                      : nd.distance_to_trigger != null ? num(nd.distance_to_trigger, 4) : "—"}
               cls={nd.spread_gate_applies === false ? "text-slate-600"
                    : nd.distance_to_trigger != null && nd.distance_to_trigger <= 0 ? "text-[#00C805]" : undefined} />
        <Field label="Trade Trigger %" value={`${trade.trade_trigger_pct ?? nd.trigger_pct}%`} />
        <Field label="Per-Level Step %" value={trade.per_level_trigger_pct != null ? `${num(trade.per_level_trigger_pct, 2)}%` : `${nd.trigger_pct}%`} />
        <Field label={trade.pair_execution_enabled ? "Required Lots at Level" : "Next Required Lots"} value={nd.required_lots} />
        <Field label="Affordable Lots" value={nd.affordable_lots} cls={!nd.fundable ? "text-[#FF6b62]" : undefined} />
        <Field label="Qty / Lot" value={num(nd.qty_per_lot, 0)} />
        <Field label={trade.pair_execution_enabled ? "Next Pair Size" : "Next Order Qty"} value={trade.pair_execution_enabled ? "1 strategy lot" : num(nd.required_qty, 0)} />
      </div>

      <div className="term-panel rounded-sm p-2.5 mb-3" data-testid={`paper-capital-${trade.slot}`}>
        <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Strategy Capital · Shared Gate A</div>
        <div className="grid grid-cols-3 gap-3">
          <div data-testid={`broker-funds-${trade.slot}`}>
            <Field label="Zerodha Broker Available Funds" value={brokerFundsText(trade.broker_funds)}
                   cls={trade.broker_funds?.available != null ? "text-[#5b9bff]" : "text-slate-500"} />
            <div className="text-[9px] text-slate-500 mt-0.5">{brokerFundsSub(trade.broker_funds)}</div>
          </div>
          <Field label="Trade Allocated Capital" value={inr(trade.paper_allocated_capital ?? trade.allocated_funds)} />
          <Field label="Trade Remaining Allocation" value={inr(trade.paper_available_funds ?? trade.funds_remaining)} />
          <Field label="Hedged capital used" value={inr(trade.paper_capital_used ?? trade.funds_used)} />
          {/* How much of the allocation the hedged book can EVER hold at once under the buffer.
              Not the allocated capital — that is what the user configured. */}
          <Field label="Maximum Hedged Capital Utilisable"
                 value={trade.max_hedged_capital_utilisable != null
                   ? `${inr(trade.max_hedged_capital_utilisable)} · ${trade.max_pairs_affordable} pairs`
                   : "—"} />
          <Field label="Margin buffer"
                 value={trade.margin_buffer_multiple != null ? `${num(trade.margin_buffer_multiple, 2)}×` : "—"} />
          <Field label="Open hedged pairs" value={trade.open_hedged_pairs ?? "—"} />
        </div>
      </div>

      {!trade.pair_execution_enabled && <div className="term-panel rounded-sm p-2.5 mb-3">
        <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Broker Execution Margin vs Hedged Margin (Zerodha-derived, affordability compares against Paper Capital above)</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Broker Execution Margin" value={nd.execution_funds_required != null ? inr(nd.execution_funds_required) : "Margin data unavailable"}
                 cls={nd.execution_funds_blocked ? "text-[#FF6b62]" : undefined} />
          <Field label="Broker Hedged Margin" value={nd.hedged_margin_required != null ? inr(nd.hedged_margin_required) : "—"} />
          <Field label="Margin Benefit" value={nd.margin_benefit != null ? inr(nd.margin_benefit) : "—"} cls="text-[#00C805]" />
          <Field label="Ladder Plan Funds Available" value={inr(nd.available_funds)} />
          <Field label="Ladder Plan Funds After Execution" value={nd.funds_after_execution != null ? inr(nd.funds_after_execution) : "—"} />
        </div>
        {nd.margin_source === "BROKER" && (nd.sell_standalone_margin != null || nd.buy_standalone_margin != null) && (
          <div className="text-[10px] text-slate-600 mt-1.5">
            SELL standalone {inr(nd.sell_standalone_margin)} · BUY standalone {inr(nd.buy_standalone_margin)} · Basket initial {inr(nd.basket_initial_margin)} · Basket final {inr(nd.basket_final_margin)}
          </div>
        )}
        {nd.execution_funds_blocked && (
          <div className="mt-2 text-[11px] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-2">
            <div className="font-bold text-[#FF6b62] mb-1">BLOCKED — INSUFFICIENT EXECUTION FUNDS</div>
            <div className="text-slate-400">Required (broker) <span className="mono text-slate-200">{inr(nd.execution_funds_required)}</span></div>
            <div className="text-slate-400">Available (paper capital) <span className="mono text-slate-200">{inr(nd.available_funds)}</span></div>
            <div className="text-slate-400">Shortfall <span className="mono text-[#FF6b62]">{inr(nd.execution_shortfall)}</span></div>
          </div>
        )}
      </div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-3">
        <div className="term-panel rounded-sm p-2.5">
          <div className="text-[9px] uppercase tracking-wide text-[#FF6b62] font-bold mb-1">SELL</div>
          <div className="text-xs mono text-slate-100 break-all">{nd.sell_symbol}</div>
          <div className="text-[11px] text-slate-400 mt-1">LTP {num(trade.sell_ltp, 2)}</div>
          <div className="text-[11px] text-slate-500">Entry Limit: <span className="italic">Pending at trigger</span></div>
        </div>
        <div className="term-panel rounded-sm p-2.5">
          <div className="text-[9px] uppercase tracking-wide text-[#5b9bff] font-bold mb-1">BUY</div>
          <div className="text-xs mono text-slate-100 break-all">{nd.buy_symbol}</div>
          <div className="text-[11px] text-slate-400 mt-1">LTP {num(trade.buy_ltp, 2)}</div>
          <div className="text-[11px] text-slate-500">Entry Limit: <span className="italic">Pending at trigger</span></div>
        </div>
      </div>

      <div className="term-panel rounded-sm p-2.5">
        <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-1.5">Execution Check</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
          <CheckRow ok={checks.strategy_running} label="Strategy Running" />
          <CheckRow ok={true} label={`Execution Environment: ${trade.environment === "SHADOW" ? "SHADOW LIVE" : trade.environment || "PAPER"}`} />
          <CheckRow ok={checks.market_data_available} label="Market Data Available" />
          <CheckRow ok={checks.previous_level_complete} label="Previous Level Complete" />
          <CheckRow ok={checks.funds_available} label="Funds Available" />
          <CheckRow ok={checks.edge_gate_passed} label="Edge Gate Passed (Q > K1)" skip={checks.edge_gate_passed === null} />
          <CheckRow ok={checks.spread_trigger_reached} label="Spread Trigger Reached (levels 2+ only)" skip={checks.spread_trigger_reached === null} />
          <CheckRow ok={checks.not_reset_active} label="Not Mid-Reset" />
        </div>
        {/* NEXT-PAIR AFFORDABILITY (Gate A) — the verified margin sheet, one pair at a time.
            Replaces a banner that showed the V2.5 ladder's whole-LEVEL figure as though it were
            what the next pair needed; it was routinely a number ten times too large, and it
            said BLOCKED while the engine was happily entering. For LIVE and SHADOW the broker's
            own gate is reported separately and is not merged into this. */}
        {npa && !npa.allowed && (
          <div className="mt-2 text-[11px] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-2"
               data-testid={`next-pair-blocked-${trade.slot}`}>
            <div className="font-bold text-[#FF6b62] mb-1">
              {npa.reason === "PAIR_ECONOMICS_UNAVAILABLE"
                ? "BLOCKED — PAIR ECONOMICS UNAVAILABLE"
                : "BLOCKED — MARGIN BUFFER"}
            </div>
            {npa.reason === "PAIR_ECONOMICS_UNAVAILABLE" ? (
              <div className="text-slate-400">
                No execution/hedged margin known for this pair — entry is refused rather than guessed.
              </div>
            ) : (
              <>
                <div className="text-slate-400">Current remaining <span className="mono text-slate-200">{inr(npa.capital_remaining)}</span></div>
                <div className="text-slate-400">Next hedged margin <span className="mono text-slate-200">− {inr(npa.hedged_per_pair)}</span></div>
                <div className="text-slate-400">Projected remaining <span className="mono text-slate-200">{inr(npa.projected_remaining)}</span></div>
                <div className="text-slate-400">Required buffer ({num(npa.buffer_multiple, 2)}×) <span className="mono text-slate-200">{inr(npa.required_buffer)}</span></div>
                <div className="text-slate-400">Buffer shortfall <span className="mono text-[#FF6b62]">{inr(Math.abs(npa.buffer_surplus_or_shortfall || 0))}</span></div>
                <div className="text-slate-600 mt-1">Next pair: #{npa.next_pair_no} across the trade · hedged capital used {inr(npa.capital_used)} of {inr(npa.allocated)}</div>
              </>
            )}
          </div>
        )}
        {npa && npa.allowed && (
          <div className="mt-2 text-[11px] text-slate-500" data-testid={`next-pair-allowed-${trade.slot}`}>
            Capital check for the next pair (#{npa.next_pair_no} across the trade{nd.level_number ? `, L${nd.level_number} pair ${nd.next_pair ?? "—"}` : ""}): ALLOWED · projected remaining <span className="mono text-slate-300">{inr(npa.projected_remaining)}</span> vs
            required buffer <span className="mono text-slate-300">{inr(npa.required_buffer)}</span>
            {" "}(surplus <span className="mono text-[#00C805]">{inr(npa.buffer_surplus_or_shortfall)}</span>).
          </div>
        )}
        {!nd.eligible && (
          false ? null : (
            <div className="mt-2 text-[11px] text-slate-500">
              Waiting: {readableReasons.join(", ") || "conditions not yet met"}.
              {checks.spread_trigger_reached === false && (
                <> Waiting for spread ≥ <span className="mono text-slate-300">{num(nd.trigger_spread, 4)}</span>, current <span className="mono text-slate-300">{num(trade.current_spread, 4)}</span>{nd.distance_to_trigger != null && <>, remaining <span className="mono text-slate-300">{num(nd.distance_to_trigger, 4)}</span></>}.</>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

/** TAB 2 — NEXT ORDER PREVIEW. */
function NextOrderPreviewTab({ trade }) {
  const nd = trade.next_level_detail;
  if (!trade.assigned_commodity || !nd) {
    return <div className="text-xs text-slate-500 p-3">No pending order to preview.</div>;
  }
  if (trade.pair_execution_enabled) return <div className="term-panel rounded-sm p-3 text-xs space-y-2">
    {nd.is_entering && entryBlock(trade)
      ? <EntryBlockBanner block={entryBlock(trade)} testId={`preview-entry-block-${trade.slot}`} />
      : <div>L{nd.level_number} · Next pair {nd.next_pair ?? "—"} · {nd.gate_a_state}</div>}
    <div>SELL {nd.sell_symbol} · BUY {nd.buy_symbol}</div>
    <div>One strategy lot per pair · FAR first, then NEAR</div>
    <div>Execution margin / pair {inr(nd.next_pair_affordability?.execution_per_pair)} · Hedged margin / pair {inr(nd.next_pair_affordability?.hedged_per_pair)}</div>
    <div>Broker execution state is reported separately.</div>
  </div>;
  return (
    <div className="term-panel rounded-sm p-3 text-[11px] space-y-1">
      <div><span className="text-slate-500">SELL symbol</span> <span className="mono text-slate-100 float-right">{nd.sell_symbol}</span></div>
      <div><span className="text-slate-500">SELL qty</span> <span className="mono text-slate-100 float-right">{num(nd.required_qty, 0)}</span></div>
      <div><span className="text-slate-500">SELL order type</span> <span className="mono text-slate-100 float-right">MARKET (PaperBroker fills at live bid, matching Excel Far-First sequencing)</span></div>
      <div><span className="text-slate-500">SELL calculated limit</span> <span className="mono text-slate-100 float-right italic">Pending at trigger</span></div>
      <div className="border-t border-[#20242c] my-1.5" />
      <div><span className="text-slate-500">BUY symbol</span> <span className="mono text-slate-100 float-right">{nd.buy_symbol}</span></div>
      <div><span className="text-slate-500">BUY qty</span> <span className="mono text-slate-100 float-right">{num(nd.required_qty, 0)}</span></div>
      <div><span className="text-slate-500">BUY order type</span> <span className="mono text-slate-100 float-right">MARKET (PaperBroker fills at live ask)</span></div>
      <div><span className="text-slate-500">BUY calculated limit</span> <span className="mono text-slate-100 float-right italic">Pending at trigger</span></div>
      <div className="border-t border-[#20242c] my-1.5" />
      <div><span className="text-slate-500">Trigger Spread</span> <span className="mono text-slate-100 float-right">{num(nd.trigger_spread, 4)}</span></div>
      <div><span className="text-slate-500">Broker Execution Margin</span> <span className="mono text-slate-100 float-right">{nd.execution_funds_required != null ? inr(nd.execution_funds_required) : "Margin data unavailable"}</span></div>
      <div><span className="text-slate-500">Broker Hedged Margin (projected)</span> <span className="mono text-slate-100 float-right">{nd.hedged_margin_required != null ? inr(nd.hedged_margin_required) : "—"}</span></div>
      <div><span className="text-slate-500">Margin Source</span> <span className="mono text-slate-100 float-right">{MARGIN_SOURCE_LABEL[nd.margin_source] || nd.margin_source}</span></div>
      <div className="border-t border-[#20242c] my-1.5" />
      <div><span className="text-slate-500">Projected Total Lots</span> <span className="mono text-slate-100 float-right">{(trade.entered_levels || 0) + nd.required_lots}</span></div>
      <div><span className="text-slate-500">Projected Total Qty</span> <span className="mono text-slate-100 float-right">{num(trade.levels.reduce((a, l) => a + (l.sell_quantity || 0), 0) + nd.required_qty, 0)}</span></div>
      <div><span className="text-slate-500">Projected Weighted Average</span> <span className="mono text-slate-600 float-right italic">Not calculated by this engine</span></div>
    </div>
  );
}

/** TAB 3 — AVERAGING PLAN. */
function AveragingPlanTab({ trade }) {
  if (trade.pair_execution_enabled) return <div>
    <CapacityPanel plan={trade.capacity_plan} />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-3">
      <Field label="Current / Running Spread" value={num(trade.current_spread, 4)} />
      <Field label="Reference Spread" value={num(trade.reference_spread, 4)} />
      <Field label="Actual Average Entry Spread" value={num(trade.actual_average_entry_spread, 4)} />
      <Field label="Planned Final Average Spread" value={num(trade.planned_final_average_spread, 4)} />
    </div>
    <LadderTable ladderPlan={trade.ladder_plan} nextLevelNumber={trade.next_level_number} block={entryBlock(trade)} />
    <PairProgressPanel trade={trade} />
    <PairEconomicsPanel trade={trade} />
  </div>;
  const tc = trade.tradable_capacity;
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Field label="Configured Levels" value={tc?.configured_levels ?? (trade.ladder_plan || []).length ?? 10} />
        {/* Engine funds gate: E(n) > AI(n), E(n+1) = E(n) - M*G(n) — the same recursion
            that admits levels, so this can no longer read lower than Executed Levels. */}
        <Field label="Tradable Levels" value={tc?.max_tradable_level != null ? tc.max_tradable_level : "—"} />
        <Field label="Executed Levels" value={trade.entered_levels || 0} />
        <Field label="Per-Level Trigger %" value={trade.per_level_trigger_pct != null ? `${num(trade.per_level_trigger_pct, 2)}%` : "—"} />
      </div>
      {/* Informational only — neither figure gates anything. "Ladder sized for" is the N
          that r = Trigger% / N was divided by; the peak funding is the pre-hedge cash a
          broker would need blocked with nothing released, which is how a trade can commit
          more than its allocation. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        {trade.ladder_sizing_levels != null && (
          <Field label="Ladder Sized For (N)" value={`${trade.ladder_sizing_levels} levels`} />
        )}
        <Field label="Peak Execution Funding Required"
               value={tc?.peak_execution_funding_required != null ? inr(tc.peak_execution_funding_required) : "—"}
               cls={tc?.peak_execution_funding_required > (trade.paper_allocated_capital ?? trade.allocated_funds)
                    ? "text-[#FFCC00]" : undefined} />
        <Field label="Cumulative Execution Funding Covers"
               value={tc?.cumulative_execution_level != null ? `${tc.cumulative_execution_level} levels` : "—"} />
        <Field label="Capacity Rule" value={tc?.capacity_rule === "ENGINE_FUNDS_GATE" ? "Engine funds gate" : (tc?.capacity_rule || "—")} />
      </div>
      {tc?.peak_execution_funding_required != null
        && tc.peak_execution_funding_required > (trade.paper_allocated_capital ?? trade.allocated_funds) && (
        <div className="text-[11px] text-[#FFCC00] mb-3" data-testid="peak-funding-warning">
          Peak execution funding for levels 1–{tc.max_tradable_level} is {inr(tc.peak_execution_funding_required)},
          above the {inr(trade.paper_allocated_capital ?? trade.allocated_funds)} allocated. The workbook funds gate
          charges margin against the balance but tests against full funds, so the ladder commits more than the
          allocation. Informational — execution follows the workbook.
        </div>
      )}
      <AveragingPlanSummary ladderPlan={trade.ladder_plan} nextLevelNumber={trade.next_level_number} enteredLevels={trade.entered_levels} />
      <LadderTable ladderPlan={trade.ladder_plan} nextLevelNumber={trade.next_level_number} block={entryBlock(trade)} />
      <PairProgressPanel trade={trade} />
      <PairEconomicsPanel trade={trade} />
    </div>
  );
}

const PLAN_TABS = [
  { key: "next", label: "Next Execution", icon: Crosshair },
  { key: "preview", label: "Next Order Preview", icon: ListChecks },
  { key: "ladder", label: "Averaging Plan", icon: ChevronsRight },
  { key: "details", label: "Trade Details", icon: ClipboardList },
  { key: "info", label: "Additional Information", icon: Info },
  { key: "audit", label: "Audit", icon: History },
];

/** Section 8/9 — Trade Plan modal, opened from the trade card's "Trade Plan" button. */
export function TradePlanModal({ trade, onClose, onEditSettings, onAssign }) {
  const [tab, setTab] = useState("next");
  // Same rule the card's former "More › Change Opportunity" item used.
  const canAssign = !["RUNNING", "EXITING", "COMPLETE"].includes(trade.status);
  return (
    <TerminalDrawer title={`${trade.name} · Trade Plan`} onClose={onClose} width={1040} testId={`trade-plan-modal-${trade.slot}`}>
        <div className="sticky top-0 bg-[#12151C] z-10">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <button onClick={onEditSettings} className="btn-x text-slate-300" data-testid={`edit-settings-${trade.slot}`}><Settings className="h-4 w-4" /> Edit Settings</button>
            {onAssign && (
              <button onClick={onAssign} disabled={!canAssign} data-testid={`assign-btn-${trade.slot}`}
                title={canAssign ? "Assign a different opportunity to this trade" : "Only while the trade is not running, exiting or complete"}
                className="btn-x btn-secondary">Change Opportunity</button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mb-3">{trade.assigned_commodity || "—"} · {trade.trade_trigger_pct != null ? `Ladder trigger ${trade.trade_trigger_pct}%` : "Ladder"}</div>
          <div className="flex gap-1 overflow-x-auto border-b border-[#2A2F3A]">
            {PLAN_TABS.map((t) => (
              <button key={t.key} onClick={() => setTab(t.key)} aria-pressed={tab === t.key} data-testid={`plan-tab-${t.key}`}
                className={`text-[11px] font-bold px-3 py-2 flex shrink-0 whitespace-nowrap items-center gap-1.5 border-b-2 -mb-px ${tab === t.key ? "border-[#2563EB] text-slate-100" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
                <t.icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="pt-4">
          {tab === "next" && <NextExecutionTab trade={trade} />}
          {tab === "preview" && <NextOrderPreviewTab trade={trade} />}
          {tab === "ladder" && <AveragingPlanTab trade={trade} />}
          {tab === "details" && <TradeDetailsTab trade={trade} />}
          {tab === "info" && <AdditionalInfoTab trade={trade} />}
          {tab === "audit" && <AuditLog trade={trade} />}
        </div>
    </TerminalDrawer>
  );
}

const cfgInput = "w-full bg-[#0A0C10] border border-[#2A2F3A] rounded-sm px-2.5 py-1.5 text-sm mono outline-none focus:border-[#2563EB]";

/** Where a per-pair figure came from. Always rendered beside the value — a manual override
 *  must never be mistaken for a broker-confirmed number, or the reverse. */
const SRC_STYLE = {
  ZERODHA: "bg-[#00C805]/15 text-[#00C805]",
  MANUAL_OVERRIDE: "bg-[#7c3aed]/20 text-[#c4b5fd]",
  ENGINE_ESTIMATE: "bg-[#FFCC00]/15 text-[#FFCC00]",
  UNAVAILABLE: "bg-[#2A2F3A] text-slate-500",
};
export function CapacityPanel({ plan }) {
  if (!plan) return null;
  return <div className="term-panel rounded-sm p-3 my-3" data-testid="gate-a-capacity">
    <div className="text-xs font-bold mb-2">Affordable Capacity · Shared Gate A</div>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      <Field label="Allocated Capital" value={inr(plan.allocated)} />
      <Field label="Execution Margin / Pair" value={inr(plan.execution_per_pair)} />
      <Field label="Hedged Margin / Pair" value={inr(plan.hedged_per_pair)} />
      <Field label="Margin Buffer" value={`${plan.buffer_multiple}×`} />
      <Field label="Required Residual Buffer" value={inr(plan.required_buffer)} />
      <Field label="Maximum Affordable Pairs" value={plan.max_affordable_pairs ?? "—"} />
      <Field label="Maximum Hedged Capital Utilisable" value={inr(plan.max_hedged_capital_utilisable)} />
      <Field label="Residual Capital at Maximum Utilisation" value={inr(plan.residual_capital)} />
      <Field label="Fully Affordable Levels" value={plan.fully_affordable_levels ?? "—"} />
      <Field label="Final Partial Level" value={plan.final_partial_level ? `L${plan.final_partial_level}` : "—"} />
      <Field label="Planned Final Average Spread" value={num(plan.planned_final_average_spread, 4)} />
      <Field label="Execution / Hedged Margin Source" value={`${plan.sources?.funds_per_pair || "UNAVAILABLE"} / ${plan.sources?.margin_per_pair || "UNAVAILABLE"}`} />
    </div>
    <div className="text-[11px] text-slate-400 mt-2">{plan.levels.map(l => `L${l.level_number}: ${l.affordable_lots ?? "—"}/${l.required_lots}`).join(" · ")}</div>
  </div>;
}

function SourceBadge({ source, locked }) {
  const src = source || "UNAVAILABLE";
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`text-[9px] px-1 py-0.5 rounded-sm font-bold ${SRC_STYLE[src] || SRC_STYLE.UNAVAILABLE}`}>{src.replace("_", " ")}</span>
      {locked && <span className="text-[9px] text-[#c4b5fd]" title="Manually saved — automatic refresh will not overwrite it">LOCKED</span>}
    </span>
  );
}

/** What the Trigger % means: a % of the SPREAD (FAR − NEAR), spread over the ladder's levels.
 *  Shown in points, as a % of the FAR contract's LTP and in rupees per pair. Display only. */
export function TriggerExplain({ te, testId }) {
  if (!te) return null;
  const id = testId || "trigger-explain";
  return (
    <div className="mono text-[11px] text-fg-secondary" data-testid={id}>
      <div>
        <span className="font-bold text-fg">Trigger {te.trade_pct}% of spread</span>
        {te.total_points != null && <> → {num(te.total_points, 2)} pts ({num(te.anchor_spread, 2)} → {num(te.final_trigger, 2)})</>}
        {" · "}{num(te.per_level_pct, 4)}% per level ≈ <span className="text-fg">{num(te.step_points, 2)} pts</span>
        {te.step_pct_of_ltp != null && <> = <span className="text-fg">{num(te.step_pct_of_ltp, 4)}% of FAR LTP</span> ₹{num(te.far_ltp, 2)}</>}
        {" · "}<span className="text-fg">₹{num(te.step_value_per_pair, 2)}</span> per pair per level
        {te.ticks_per_level != null && <> · tick ₹{te.tick_size} → {num(te.ticks_per_level, 2)} ticks per level</>}
      </div>
      {te.warning && <div className="text-[#FF9F0A]" data-testid={`${id}-warning`}>⚠ {te.warning}</div>}
    </div>
  );
}

/** PHASE 4 — pair-by-pair ENTRY progress. Renders nothing for trades still on the
 *  level-sized path, so existing trades look exactly as they did. */
export function PairProgressPanel({ trade }) {
  const pp = trade.pair_progress;
  if (!trade.pair_execution_enabled || !pp) return null;
  const rows = pp.levels || [];
  return (
    <div className="term-panel-2 rounded-sm p-2.5 mt-3" data-testid="pair-progress-panel">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wide">Pair Execution</span>
        <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-[#7c3aed]/20 text-[#c4b5fd]">PAIR-BY-PAIR ENTRY</span>
      </div>
      {/* A pair holding a FAR position that NEAR did not cover. Nothing on this screen matters
          more: it is a naked futures leg, and it is why new entries have stopped. */}
      {pp.unhedged && (
        <div className="mb-2 rounded-sm border border-[#FF3B30] bg-[#FF3B30]/10 p-2" data-testid="pair-unhedged-banner">
          <div className="mono text-[12px] font-bold text-[#FF6B60]">
            ⚠ UNHEDGED POSITION — L{pp.unhedged.level_number} pair{pp.unhedged.pairs?.length > 1 ? "s" : ""} {(pp.unhedged.pairs || []).join(", ")}
          </div>
          <div className="mono text-[11px] text-slate-200 mt-0.5">
            {pp.unhedged.naked_quantity ? `${pp.unhedged.naked_quantity} contract(s) of the FAR leg are not covered by a NEAR leg. ` : ""}
            New entries are stopped for this account. Exits and monitoring keep running.
          </div>
          <div className="text-[10px] text-slate-400 mt-0.5">
            {pp.unhedged.recovery_state || "MANUAL_INTERVENTION"}
            {pp.unhedged.reason ? ` · ${pp.unhedged.reason}` : ""}
            {pp.unhedged.since ? ` · since ${new Date(pp.unhedged.since).toLocaleString()}` : ""}
          </div>
        </div>
      )}
      {pp.in_flight && (
        <div className="mono text-[12px] text-[#FFCC00] mb-2" data-testid="pair-in-flight">
          {pp.in_flight.label} · {pp.in_flight.current_pair_label}
          {pp.in_flight.near_wait && (
            <div className="text-[11px] text-[#FF6B60] mt-0.5" data-testid="pair-near-wait">
              ⚠ {pp.in_flight.near_wait.text}
              {pp.in_flight.near_wait.since ? ` · since ${new Date(pp.in_flight.near_wait.since).toLocaleString()}` : ""}
            </div>
          )}
          {pp.in_flight.entry_pause && (
            <div className="text-[11px] text-[#FF9F0A] mt-0.5" data-testid="pair-entry-paused">
              PAUSED — spread below trigger {num(pp.in_flight.entry_pause.trigger_spread, 4)}
              {pp.in_flight.entry_pause.spread != null ? ` (was ${num(pp.in_flight.entry_pause.spread, 4)})` : ""}
              {" "}· pair {pp.in_flight.entry_pause.pair} of {pp.in_flight.pairs_required} waits; pairs already entered are kept
            </div>
          )}
        </div>
      )}
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-500">No pair-executed level yet.</div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => {
            const pct = r.pairs_required ? (r.pairs_complete / r.pairs_required) * 100 : 0;
            const done = r.level_status === "OPEN";
            return (
              <div key={r.level_number} className="flex items-center gap-2" data-testid={`pair-progress-L${r.level_number}`}>
                <span className="mono text-[11px] text-slate-300 w-44">{r.label}</span>
                <div className="flex-1 h-1.5 bg-[#0A0C10] rounded-sm overflow-hidden">
                  <div className={`h-full ${done ? "bg-[#00C805]" : "bg-[#FFCC00]"}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="mono text-[10px] text-slate-500 w-52 text-right">
                  {done ? "LEVEL COMPLETE" : (r.current_pair_label || r.level_status)}
                </span>
              </div>
            );
          })}
        </div>
      )}
      <div className="text-[10px] text-slate-500 mt-2">
        Strategy conditions (spread trigger, edge, pair count) are latched when the level triggers.
        Operational conditions are re-checked before every FAR and every NEAR order.
      </div>
    </div>
  );
}

/** THE PHASE 4 CONTROL — per trade, default ON for new trades (Level Entry still selectable).
 * Only changeable while the trade is stopped. */
export function PairExecutionToggle({ trade, onChanged }) {
  const [busy, setBusy] = useState(false);
  const on = !!trade.pair_execution_enabled;
  const blocked = trade.status === "RUNNING";
  const flip = async () => {
    setBusy(true);
    try {
      await api.post(`/paper/v25/trades/${trade.id}/pair-execution`, { enabled: !on });
      toast.success(`Pair-by-pair entry ${!on ? "ENABLED" : "disabled"} for ${trade.assigned_commodity}`);
      onChanged?.();
    } catch (e) { toast.error(detailText(e, "Could not change execution mode")); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2" data-testid="pair-execution-toggle">
      <span className={`text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${on ? "bg-[#7c3aed]/20 text-[#c4b5fd]" : "bg-[#2A2F3A] text-slate-400"}`}>
        {on ? "PAIR ENTRY" : "LEVEL ENTRY"}
      </span>
      <button disabled={busy || blocked} onClick={flip} data-testid="pair-execution-btn"
        title={blocked ? "Stop the trade before changing execution granularity" : "Switch entry granularity for this trade"}
        className="text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#2A2F3A] hover:bg-[#39404d] disabled:opacity-40">
        {on ? "USE LEVEL ENTRY" : "USE PAIR ENTRY"}
      </button>
    </div>
  );
}

/** Section: per-instrument pair economics (persistent) + the Phase 2 shadow funding model.
 *  Read-only here apart from the two overrides; nothing on this panel gates execution. */
export function PairEconomicsPanel({ trade, onChanged }) {
  const pf = trade.pair_funding;
  const cfg = pf?.config;
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ funds_per_pair: "", margin_per_pair: "" });

  if (!pf) return null;

  const save = async (field) => {
    const value = parseFloat(draft[field]);
    if (!(value > 0)) { toast.error("Enter a value greater than 0"); return; }
    setBusy(true);
    try {
      await api.post(`/instrument-config/${cfg.segment}/${cfg.underlying}/override`, { field, value });
      toast.success(`${field.replace(/_/g, " ")} saved — it will not be overwritten automatically`);
      setDraft((d) => ({ ...d, [field]: "" }));
      onChanged?.();
    } catch (e) { toast.error(detailText(e, "Save failed")); }
    setBusy(false);
  };

  const retake = async (field) => {
    setBusy(true);
    try {
      await api.post(`/paper/v25/trades/${trade.id}/pair-economics/refresh${field ? `?field=${field}` : ""}`);
      toast.success(field ? `${field.replace(/_/g, " ")} re-taken from Zerodha` : "Refreshed from Zerodha");
      onChanged?.();
    } catch (e) { toast.error(detailText(e, "Refresh failed")); }
    setBusy(false);
  };

  const Row = ({ field, label, hint }) => (
    <div className="term-panel-2 rounded-sm p-2.5">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[11px] text-slate-400 uppercase tracking-wide">{label}</span>
        <SourceBadge source={cfg?.[`${field}_source`]} locked={cfg?.[`${field}_locked`]} />
      </div>
      <div className="mono text-base text-slate-100">{cfg?.[field] != null ? inr(cfg[field]) : "—"}</div>
      <div className="text-[10px] text-slate-500 mt-0.5">{hint}</div>
      <div className="flex gap-1.5 mt-2">
        <input className={cfgInput} placeholder="override…" value={draft[field]}
               onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
               data-testid={`override-input-${field}`} />
        <button disabled={busy} onClick={() => save(field)} data-testid={`override-save-${field}`}
                className="text-[11px] font-bold px-2.5 rounded-sm bg-[#2563EB] disabled:opacity-40">SAVE</button>
        <button disabled={busy} onClick={() => retake(field)} title="Re-take this value from Zerodha (clears the lock)"
                className="text-[11px] font-bold px-2.5 rounded-sm bg-[#2A2F3A] disabled:opacity-40">↻</button>
      </div>
    </div>
  );

  return (
    <div className="mt-4" data-testid="pair-economics-panel">
      <div className="text-[11px] font-bold text-slate-300 uppercase tracking-wide mb-2">
        Pair Economics · {cfg?.segment}:{cfg?.underlying}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Row field="funds_per_pair" label="Funds Needed per Pair"
             hint="Naked FAR leg alone — the peak while a pair is mid-flight" />
        <Row field="margin_per_pair" label="Margin Required per Pair"
             hint="Hedged FAR+NEAR basket — what stays blocked" />
      </div>
      {pf.contracts_drifted && (
        <div className="text-[11px] text-[#FFCC00] mt-2">
          These figures were captured on a different contract pair. Re-take them from Zerodha before relying on them.
        </div>
      )}

      <div className="text-[11px] text-slate-400 mt-3">{trade.pair_execution_enabled ? "Pair economics supply shared Gate A. Broker execution checks remain separate." : "Pair economics are informational while level entry is selected."}</div>
    </div>
  );
}

/** Section 1/4/11 — Trade Configuration modal: shared by "assign" (new trade) and
 * "edit settings" (existing trade) flows. Backend (`/trades/preview-config`) computes the
 * derived preview; this component only formats/displays it. */
// `environments`: the New Trade workflow passes the environments the user's account is authorised
// for (server-decided, /paper/v25/config -> new_trade.environments). When given, ONLY those are
// offered - PAPER is not part of that workflow. Callers that omit it keep the original list.
export function TradeConfigModal({ trade, commodity, mode, opportunity, onClose, onConfirm, canLive = false, environments = null }) {
  const [allocated, setAllocated] = useState(String(trade.allocated_funds ?? ""));
  const [triggerPct, setTriggerPct] = useState(trade.trade_trigger_pct != null ? String(trade.trade_trigger_pct) : String(trade.trigger_pct ?? 10));
  const [profitPct, setProfitPct] = useState(trade.profit_pct != null ? String(trade.profit_pct) : "");
  // New trades (and trades created since Phase 0) target Profit % of their INVESTED value — the
  // capital the open book actually uses — so Profit % is mandatory and there is no G24 fallback.
  const investedBasis = mode !== "edit" || trade.target_basis === "INVESTED";
  const customTarget = investedBasis || trade.ladder_mode === "CUSTOM_CONFIGURED";
  const [targetAmount, setTargetAmount] = useState(targetRupees(trade.allocated_funds, trade.profit_pct));
  const editAllocated = (value) => {
    setAllocated(value);
    setTargetAmount(targetRupees(value, profitPct));
  };
  const editProfitPct = (value) => {
    setProfitPct(value);
    setTargetAmount(targetRupees(allocated, value));
  };
  const editTargetAmount = (value) => {
    setTargetAmount(value);
    if (value === "" || (Number.isFinite(Number(value)) && Number(value) >= 0)) {
      setProfitPct(targetPercent(allocated, value));
    }
  };
  const [edgeThreshold, setEdgeThreshold] = useState(String(trade.edge_threshold ?? ""));
  // The execution environment is chosen once, when the trade is created, and never changes
  // afterwards. PAPER fills internally; LIVE sends real orders to Zerodha; SHADOW LIVE sends the
  // SAME real orders to an unfunded validation account so they are refused, and then advances
  // the strategy on a simulated fill at the intended limit price.
  const envChoices = Array.isArray(environments) ? environments : null;
  const [environment, setEnvironment] = useState(
    envChoices ? (envChoices.includes(trade.trading_environment) ? trade.trading_environment : (envChoices[0] || ""))
      : (trade.trading_environment || "PAPER"));
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewRevision, setPreviewRevision] = useState(0);
  const [saveError, setSaveError] = useState(null);
  const [busy, setBusy] = useState(false);
  const targetCommodity = commodity || trade.assigned_commodity;
  // Which Analysier model the preview must resolve the contracts through. The create flow
  // supplies it on the opportunity; edit/assign read it off the trade. Left undefined the
  // backend defaults to MCX, which is what every pre-NFO caller expected.
  const targetMarket = opportunity?.market_segment || trade.market_segment || null;

  useEffect(() => {
    let active = true;
    setPreview(null);
    setPreviewErr(null);
    setPreviewLoading(false);
    const af = parseFloat(allocated), tp = parseFloat(triggerPct);
    const pp = profitPct === "" ? null : Number(profitPct);
    if (!targetCommodity || !Number.isFinite(af) || !(af > 0) || !Number.isFinite(tp) || !(tp > 0)
        || (pp != null && (!Number.isFinite(pp) || pp < 0))) return;
    setPreviewLoading(true);
    const t = setTimeout(() => {
      api.post("/paper/v25/trades/preview-config", {
        ...(mode === "edit" ? { trade_id: trade.id } : {}),
        commodity: targetCommodity, market: targetMarket, allocated_funds: af, trigger_pct: tp,
        profit_pct: profitPct === "" ? null : parseFloat(profitPct),
        edge_threshold: edgeThreshold === "" ? null : parseFloat(edgeThreshold),
      }).then((r) => { if (active) { setPreview(r.data); setPreviewErr(null); } })
        .catch(() => { if (active) { setPreview(null); setPreviewErr("Could not load the preview. Your entries have been kept."); } })
        .finally(() => { if (active) setPreviewLoading(false); });
    }, 400);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetCommodity, targetMarket, allocated, triggerPct, profitPct, edgeThreshold, mode, trade.id, previewRevision]);

  const errors = [];
  const af = parseFloat(allocated), tp = parseFloat(triggerPct), pp = profitPct === "" ? null : parseFloat(profitPct);
  if (!Number.isFinite(af) || !(af > 0)) errors.push("Allocated funds must be finite and greater than 0");
  if (!(tp > 0)) errors.push("Trigger % must be greater than 0");
  if (pp != null && (!Number.isFinite(pp) || pp < 0)) errors.push("Profit % must be a finite number, 0 or greater");
  if (investedBasis && pp == null) errors.push("Profit % is required — the target is Profit % of the invested value");
  if (targetAmount !== "" && (!Number.isFinite(Number(targetAmount)) || Number(targetAmount) < 0)) errors.push("Target ₹ must be a finite number, 0 or greater");
  if (edgeThreshold !== "" && isNaN(parseFloat(edgeThreshold))) errors.push("Edge threshold must be a valid number");
  if (mode === "create" && envChoices && !envChoices.includes(environment)) {
    errors.push("This account is not authorised for LIVE or SHADOW trading — ask an admin");
  }

  const confirm = async () => {
    setSaveError(null);
    setBusy(true);
    try {
      await onConfirm({
        allocated_funds: af, ...(customTarget ? { trigger_pct: tp, profit_pct: pp } : {}),
        edge_threshold: edgeThreshold === "" ? null : parseFloat(edgeThreshold),
        ...(mode === "create" ? { trading_environment: environment } : {}),
      });
    } catch (e) {
      setSaveError(refusalText(e, "Could not save. Your entries have been kept; please try again."));
    } finally { setBusy(false); }
  };

  return (
    <TerminalDrawer title={mode === "edit" ? "Edit Trade Settings" : "Configure Trade"} onClose={onClose} width={520} testId="trade-config-modal" busy={busy}>
        <div className="text-[11px] text-slate-500 mb-3">{trade.name} · {targetCommodity || "—"}{mode === "edit" && trade.entered_levels > 0 ? " · already-executed levels stay frozen; only future levels are affected" : ""}</div>

        {opportunity && (
          <div className="term-panel-2 rounded-sm p-2.5 mb-4 space-y-1.5" data-testid="config-opportunity-summary">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold bg-[#2563EB]/15 text-[#5b9bff]">{opportunity.market_segment} FUTURES</span>
              <span className={`text-[9px] px-1 rounded-sm font-bold ${opportunity.direction === "SELL_FAR_BUY_NEAR" ? "bg-[#2563EB]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>
                {opportunity.direction === "SELL_FAR_BUY_NEAR" ? "SELL FAR" : "SELL NEAR"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 mono text-[10px]">
              {/* MCX rows name the legs sell_leg/buy_leg (and mirror them as sell_symbol/buy_symbol);
                  NFO rows use sell_symbol/buy_symbol. Either is the exact contract. */}
              <div data-testid="config-sell-symbol"><span className="text-slate-500">SELL </span><span className="text-[#FF6b62]">{opportunity.sell_symbol || opportunity.sell_leg || "—"}</span></div>
              <div data-testid="config-buy-symbol"><span className="text-slate-500">BUY </span><span className="text-[#5b9bff]">{opportunity.buy_symbol || opportunity.buy_leg || "—"}</span></div>
              {opportunity.spot_symbol && <div className="col-span-2"><span className="text-slate-500">SPOT (input only) </span><span className="text-slate-400">{opportunity.spot_symbol}</span></div>}
              <div><span className="text-slate-500">Spread </span><span className="text-slate-200">{num(opportunity.winning_spread ?? Math.max(opportunity.spread_sell_far_buy_near ?? 0, opportunity.spread_buy_far_sell_near ?? 0), 4)}</span></div>
              <div><span className="text-slate-500">DTE </span><span className="text-slate-200">{opportunity.days_to_expiry}</span></div>
              <div data-testid="config-converge"><span className="text-slate-500" title="spread closes at zero; net of estimated Zerodha charges and slippage - a scenario, not a guarantee">Converge P&L (net) </span><ConvergeCell row={opportunity} /></div>
              <div><span className="text-slate-500" title="net convergence P&L / Zerodha basket margin">Return on margin </span><ReturnCell row={opportunity} /> <MarginCell row={opportunity} /></div>
              {/* Current broker/exchange lot only; the workbook lot is not part of the production display. */}
              <div><span className="text-slate-500">Lot </span><span className="text-slate-200">{opportunity.lot}</span></div>
              <div className="col-span-2"><span className="text-slate-500" title="Excel V2.5 L / M: still sizes the averaging ladder until Zerodha's margin is known for the trade - an estimate, not broker margin">Workbook estimate L / M (ladder sizing) </span><span className="text-slate-200">{inr(opportunity.funds_required)} / {inr(opportunity.margin)}</span></div>
              <div className="col-span-2"><WorkbookNote row={opportunity} /></div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label htmlFor="cfg-allocated" className="text-[11px] text-slate-400 uppercase tracking-wide">Allocated Capital</label>
            <input id="cfg-allocated" disabled={busy} value={allocated} onChange={(e) => editAllocated(e.target.value)} type="number" className={cfgInput} data-testid="cfg-allocated-funds" />
            <div className="text-[10px] text-slate-600 mt-1">
              Strategy capital allocated to this trade. Broker funds and execution checks are reported separately.
              {mode === "create" && trade.allocated_funds != null ? <> Default for a new trade: {inr(trade.allocated_funds)}.</>
                : preview?.paper_capital_default != null && <> Default: {inr(preview.paper_capital_default)}.</>}
            </div>
          </div>
          <div>
            <label htmlFor="cfg-trigger" className="text-[11px] text-slate-400 uppercase tracking-wide">Trigger %</label>
            <input id="cfg-trigger" disabled={busy} value={triggerPct} onChange={(e) => setTriggerPct(e.target.value)} type="number" className={cfgInput} data-testid="cfg-trigger-pct" />
          </div>
          <div>
            <label htmlFor="cfg-profit" className="text-[11px] text-slate-400 uppercase tracking-wide">
              {investedBasis ? "Profit % of invested value (Auto Square-Off target)" : "Profit % for Auto Square-Off"}
            </label>
            <input id="cfg-profit" value={profitPct} onChange={(e) => editProfitPct(e.target.value)} disabled={busy || !customTarget} type="number"
              placeholder={investedBasis ? "required" : "optional — leave blank to use G24"} className={cfgInput} data-testid="cfg-profit-pct" />
            {investedBasis && <div className="text-[10px] text-slate-600 mt-1">
              Target = this % of the capital the open position uses (Capital Used) plus entry and estimated exit charges — never the allocated capital. Auto Square-Off starts when Net P&amp;L at LTP reaches it.
            </div>}
          </div>
          {customTarget && !investedBasis && <div>
            <label htmlFor="cfg-target" className="text-[11px] text-slate-400 uppercase tracking-wide">Target ₹ Value</label>
            <input id="cfg-target" value={targetAmount} onChange={(e) => editTargetAmount(e.target.value)}
              onBlur={() => { if (targetAmount === "" || (Number.isFinite(Number(targetAmount)) && Number(targetAmount) >= 0)) setTargetAmount(targetRupees(allocated, profitPct)); }}
              disabled={busy || !(Number(allocated) > 0) || !Number.isFinite(Number(allocated))}
              type="number" min="0" step="0.01" placeholder="blank — uses G24"
              className={cfgInput} data-testid="cfg-target-rupees" />
          </div>}
          <div>
            <label htmlFor="cfg-edge" className="text-[11px] text-slate-400 uppercase tracking-wide">Edge Threshold (K1)</label>
            <input id="cfg-edge" disabled={busy} value={edgeThreshold} onChange={(e) => setEdgeThreshold(e.target.value)} type="number" className={cfgInput} data-testid="cfg-edge-threshold" />
          </div>
          {mode === "create" && (
            <div>
              <label htmlFor="cfg-env" className="text-[11px] text-slate-400 uppercase tracking-wide">Execution Environment</label>
              <select id="cfg-env" disabled={busy} value={environment} onChange={(e) => setEnvironment(e.target.value)} className={cfgInput} data-testid="cfg-environment">
                {envChoices ? (<>
                  {envChoices.length === 0 && <option value="">— no authorised environment —</option>}
                  {envChoices.includes("LIVE") && <option value="LIVE">LIVE — real orders to Zerodha</option>}
                  {envChoices.includes("SHADOW") && <option value="SHADOW">SHADOW LIVE — broker validation (real orders, expected rejection)</option>}
                </>) : (<>
                  <option value="PAPER">PAPER — simulated fills on live market data</option>
                  {canLive && <option value="LIVE">LIVE — real orders to Zerodha</option>}
                  {canLive && <option value="SHADOW">SHADOW LIVE — broker validation (real orders, expected rejection)</option>}
                </>)}
              </select>
              {envChoices && envChoices.length === 0 && <div className="text-[10px] text-[#FF6b62] mt-1" data-testid="cfg-env-none">This account is not enabled for LIVE or SHADOW trading. An admin enables it in Admin → Users.</div>}
              {!envChoices && !canLive && <div className="text-[10px] text-slate-500 mt-1">LIVE and SHADOW require the LIVE_TRADER role.</div>}
              {envChoices && <div className="text-[10px] text-slate-500 mt-1" data-testid="cfg-save-only-note">Add Trade only saves the configuration. Nothing is sent to Zerodha until you press Start.</div>}
              {environment === "SHADOW" && (
                <div className="text-[10px] text-[#FFCC00] mt-1" data-testid="cfg-shadow-note">
                  SHADOW LIVE / SIMULATED STRATEGY FILL / REAL BROKER REJECTION. Real Zerodha
                  orders are sent from an account with its funds withdrawn so they are refused;
                  the strategy then continues on a simulated fill at the intended limit price.
                  Requires an account enabled for shadow validation, and that account can never
                  also be LIVE enabled.
                </div>
              )}
              {environment === "LIVE" && (
                <div className="text-[10px] text-[#FF6b62] mt-1">
                  Real money. Orders are routed to Zerodha and filled by the exchange.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="term-panel rounded-sm p-3 mt-4" data-testid="cfg-preview">
          <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-2">Derived Preview</div>
          {previewErr ? <div role="alert" className="text-xs text-[#FF6b62]">{previewErr}<button className="btn-x ml-2" onClick={() => setPreviewRevision(v => v + 1)}>Retry preview</button></div> :
           previewLoading ? <div role="status" className="text-xs text-slate-400">Calculating capacity and target…</div> :
           !preview ? <div className="text-xs text-slate-600">Enter allocated funds and trigger % to see the preview.</div> :
           !preview.available ? <div className="text-xs text-[#FFCC00]">{preview.message}</div> : (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Allocated Capital" value={inr(preview.allocated_funds)} />
              <Field label="Configured Levels" value={preview.configured_levels} />
              <Field label="Fully Affordable Levels" value={preview.capacity_plan?.fully_affordable_levels ?? "—"} />
              <Field label="Trigger %" value={`${preview.trigger_pct}%`} />
              <Field label="Per-Level Trigger %" value={preview.per_level_trigger_pct != null ? `${num(preview.per_level_trigger_pct, 2)}%` : "—"} />
              <Field label="Reference Spread" value={num(preview.reference_spread, 4)} />
              {preview.trigger_explain && (
                <div className="col-span-2"><TriggerExplain te={preview.trigger_explain} testId="cfg-trigger-explain" /></div>
              )}
              <Field label="Final Reachable Trigger" value={num(preview.capacity_plan?.final_reachable_trigger, 4)} />
              <Field label="Profit %" value={preview.profit_pct != null ? `${preview.profit_pct}%` : investedBasis ? "required" : "— (uses G24)"} />
              {preview.target_basis === "INVESTED"
                ? <Field label="Target at full utilisation" value={preview.target_at_full_utilisation != null ? inr(preview.target_at_full_utilisation) : "—"} />
                : <Field label="Target P&L ₹ Value" value={inr(preview.target_pnl)} />}
              <Field label="Edge Threshold K1" value={num(preview.edge_threshold, 4)} />
              {/* LIVE exchange lot the ladder was sized on. The old workbook lot is still in the
                  API response for parity/debugging but is not shown. */}
              <Field label="Qty / Lot (live)" value={num(preview.qty_per_lot, 0)} />
            </div>
          )}
          {preview?.margin_note && <div className="text-[11px] text-[#FFCC00] mt-2">{preview.margin_note}</div>}
          <CapacityPanel plan={preview?.capacity_plan} />
        </div>

        {errors.length > 0 && (
          <div role="alert" className="mt-3 text-[11px] text-[#FF6b62] bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm p-2">
            {errors.map((e) => <div key={e}>{e}</div>)}
          </div>
        )}

        {saveError && <div role="alert" className="mt-3 text-xs text-[#FF6b62]">{saveError}</div>}
        <div className="terminal-drawer-footer">
          <button onClick={onClose} disabled={busy} className="btn-x bg-[#232730] border border-[#2A2F3A]" data-testid="cfg-cancel">Cancel</button>
          <button onClick={confirm} disabled={busy || errors.length > 0} className="btn-x bg-[#2563EB]" data-testid="cfg-confirm">
            {busy ? "Saving…" : mode === "edit" ? "Save Settings" : "Confirm & Add Trade"}
          </button>
        </div>
        <style>{`.btn-x{display:inline-flex;align-items:center;gap:4px;font-size:11px;font-weight:700;padding:5px 10px;border-radius:3px;transition:opacity .15s}.btn-x:disabled{opacity:.4;cursor:not-allowed}.btn-x:not(:disabled):hover{opacity:.85}`}</style>
    </TerminalDrawer>
  );
}

/** Section 1 — Assign Opportunity: pick a commodity, then confirm via TradeConfigModal
 * before the trade is actually assigned. */
export function AssignModal({ trade, onClose, onAssigned }) {
  const [opps, setOpps] = useState(null);
  const [error, setError] = useState(null);
  const [configuring, setConfiguring] = useState(null);   // commodity chosen, awaiting config confirm
  const [busy, setBusy] = useState(false);
  const loadOpportunities = useCallback(() => api.get("/paper/v25/opportunities")
    .then((r) => { setOpps(r.data); setError(null); })
    .catch(() => setError("Could not load opportunities. Please retry.")), []);
  useEffect(() => { loadOpportunities(); }, [loadOpportunities]);

  if (configuring) {
    return (
      <TradeConfigModal
        trade={trade} commodity={configuring} mode="assign"
        onClose={() => setConfiguring(null)}
        onConfirm={async (cfg) => {
          setBusy(true);
          try {
            await api.post(`/paper/v25/trades/${trade.id}/assign`, { commodity: configuring, ...cfg });
            toast.success(`${configuring} assigned to ${trade.name}`);
            onAssigned();
          } catch (e) { toast.error(detailText(e, "Assign failed")); throw e; }
          finally { setBusy(false); }
        }}
      />
    );
  }

  return (
    <TerminalDrawer title={`Assign Opportunity · ${trade.name}`} onClose={onClose} width={620} testId="assign-modal" busy={busy}>
        <RequestStatus error={error} onRetry={loadOpportunities} />
        <div className="text-[11px] text-slate-500 mb-3">Ranked by return on margin, highest first · live MCX data</div>
        {!opps ? (!error && <div role="status" className="text-xs text-slate-500">Loading opportunities…</div>) :
          !opps.zerodha_ready ? <div className="text-xs text-[#FFCC00]">Zerodha not connected — cannot assign.</div> :
          opps.opportunities.length === 0 ? <div className="text-xs text-slate-600">No qualifying live opportunities yet.</div> :
          <div className="space-y-1.5">
            {opps.opportunities.map((r) => (
              <div key={r.commodity} className="term-panel-2 rounded-sm p-2.5 flex items-center justify-between mono text-xs" data-testid={`opp-${r.commodity}`}>
                <div>
                  <span className="font-bold text-slate-100">#{r.rank} {r.commodity}</span>
                  <span className={`ml-2 text-[9px] px-1 rounded-sm font-bold ${r.direction === "SELL_FAR_BUY_NEAR" ? "bg-[#2563EB]/15 text-[#5b9bff]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}>{r.direction === "SELL_FAR_BUY_NEAR" ? "SELL FAR" : "SELL NEAR"}</span>
                  <div className="text-[10px] text-slate-500 mt-0.5 flex gap-2 items-center">Converge <ConvergeCell row={r} /> · <ReturnCell row={r} /> · {r.days_to_expiry}d</div>
                </div>
                <button disabled={busy} onClick={() => setConfiguring(r.commodity)} data-testid={`assign-${r.commodity}`} className="text-[10px] font-bold px-2 py-1 rounded-sm bg-[#2563EB] disabled:opacity-50">CONFIGURE</button>
              </div>
            ))}
          </div>}
    </TerminalDrawer>
  );
}

/** The trade's decision audit — same endpoint, same 3-second refresh and same rendering as the
 *  former Audit drawer. Shown in Plan › Audit. */
export function AuditLog({ trade }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const loadAudit = useCallback(() => api.get(`/paper/v25/trades/${trade.id}/audit`)
    .then((r) => { setRows(r.data.audit); setError(null); })
    .catch(() => setError("Audit updates are unavailable. Any displayed entries are from the last successful update.")), [trade.id]);
  useEffect(() => {
    loadAudit(); return visibleInterval(loadAudit, 3000);
  }, [loadAudit]);
  return (
    <div data-testid={`audit-log-${trade.slot}`}>
        <RequestStatus error={error} onRetry={loadAudit} />
        {!rows ? (!error && <div role="status" className="text-xs text-slate-500">Loading audit entries…</div>) :
          rows.length === 0 ? <div className="text-xs text-slate-600">No decisions logged yet.</div> :
          <div className="space-y-1.5">
            {rows.map((a) => (
              <div key={a.id} className="term-panel-2 rounded-sm p-2 text-[11px] mono">
                <div className="flex items-center justify-between"><span className="font-semibold text-slate-200">{a.event}</span><span className="text-slate-600">{a.ts.slice(11, 19)}</span></div>
                {a.detail && <pre className="text-[10px] text-slate-400 whitespace-pre-wrap mt-1">{JSON.stringify(a.detail, null, 1)}</pre>}
              </div>
            ))}
          </div>}
    </div>
  );
}

export function AuditDrawer({ trade, onClose }) {
  return (
    <TerminalDrawer title={`${trade.name} · Decision Audit`} onClose={onClose} width={620} testId="audit-drawer">
      <AuditLog trade={trade} />
    </TerminalDrawer>
  );
}

/** Compact "Next: L2 @ 10.20 (Δ0.35)" line for the main card — pure formatting of
 * next_level_detail, no calculation. */
function NextTriggerSummary({ trade }) {
  const nd = trade.next_level_detail;
  if (!trade.assigned_commodity) return <span className="text-slate-600">not assigned</span>;
  if (trade.status === "COMPLETE") {
    return (
      <span className="text-[#c4b5fd]">
        Cycle complete
        {/* a LIVE cycle can only reach COMPLETE through flat verification — say so */}
        {trade.square_off?.broker_flat_verified ? (
          <span className="text-[#00C805] font-semibold"> · broker confirmed flat</span>
        ) : null}
        {" · remove this trade and create a new one to trade again"}
      </span>
    );
  }
  if (trade.status === "EXITING") {
    // A LIVE square-off is not finished when the exits are sent — it is finished when Zerodha
    // says the position is gone. That wait is a state of its own, not a stall.
    const so = trade.square_off;
    if (so?.state === "EXIT_VERIFY_PENDING") {
      return (
        <span className="text-[#FF9500] font-semibold">
          Verifying broker flat… ({so.attempts}/{so.max_attempts})
          {so.reasons?.length ? <span className="text-slate-400 font-normal"> · {so.reasons[0]}</span> : null}
        </span>
      );
    }
    if (so?.state === "MANUAL_INTERVENTION") {
      return <span className="text-[#FF3B30] font-semibold">⚠ Square-off could not be verified flat — needs a human</span>;
    }
    return <span className="text-[#FF9500] font-semibold">Square-off in progress…</span>;
  }
  if (!nd) return <span className="text-[#00C805]">averaging plan complete · waiting for exit</span>;
  if (nd.is_entering) {
    const b = entryBlock(trade);
    return <span>
      <span className="text-slate-100 font-semibold">L{nd.level_number}: {nd.pairs_complete} / {nd.pairs_required}</span>
      {b ? <span className={b.state === "STALLED" ? "text-[#FF6b62]" : "text-[#FFCC00]"} data-testid={`status-entry-block-${trade.slot}`}>
             {" · "}{b.state === "STALLED" ? "STALLED" : `RETRY ${b.retries_used + 1}/${b.max_retries} PENDING`} at pair {b.pair}</span>
         : <>{" · "}Next pair {nd.next_pair ?? "—"} · {nd.gate_a_state || nd.wait_reason}</>}
    </span>;
  }
  // Level 1 has NO spread trigger in the workbook (Dashboard!N3 tests only D1, Q>K1,
  // BE5=T3, L3=0 and leg sequencing), so showing a trigger spread for L1 would name a gate
  // that does not exist. `spread_gate_applies` comes from the backend.
  const showSpread = nd.spread_gate_applies !== false;
  const reason = nd.wait_reason || (nd.eligible ? "READY TO ENTER" : "WAITING");
  const good = nd.eligible;
  return (
    <span>
      Next: <span className="text-slate-100 font-semibold">L{nd.level_number}</span>
      {showSpread ? (
        <> @ <span className="text-slate-200">{num(nd.trigger_spread, 4)}</span>
          {nd.distance_to_trigger != null && <> (Δ<span className={nd.distance_to_trigger <= 0 ? "text-[#00C805]" : "text-slate-400"}>{num(nd.distance_to_trigger, 4)}</span>)</>}</>
      ) : (
        <> · <span className="text-slate-500">gated by K1 only (no spread trigger at L1)</span></>
      )}
      <span className={`ml-2 text-[9px] px-1.5 py-0.5 rounded-sm font-bold ${good ? "bg-[#00C805]/15 text-[#00C805]" : reason.startsWith("BLOCKED") || reason.startsWith("STOPPED") ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#FFCC00]/15 text-[#FFCC00]"}`}
        data-testid={`wait-reason-${trade.slot}`}>{reason}</span>
    </span>
  );
}

/** Big, instantly readable lifecycle status. Presentation only. */
const STATUS_PILL = {
  RUNNING: { cls: "bg-[#00C805] text-black", dot: "bg-black/60 animate-pulse", label: "RUNNING" },
  STOPPED: { cls: "bg-[#FF3B30] text-white", dot: "bg-white/70", label: "STOPPED" },
  READY: { cls: "bg-[#2563EB] text-white", dot: "bg-white/70", label: "READY" },
  EXITING: { cls: "bg-[#FF9500] text-black", dot: "bg-black/60 animate-pulse", label: "EXITING" },
  RESETTING: { cls: "bg-[#FF9500] text-black", dot: "bg-black/60 animate-pulse", label: "RESETTING" },
  COMPLETE: { cls: "bg-[#7c3aed] text-white", dot: null, label: "COMPLETE" },
  ERROR: { cls: "bg-[#FF3B30] text-white", dot: "bg-white/70", label: "ERROR" },
};

export function StatusPill({ status, testId }) {
  const s = STATUS_PILL[status] || { cls: "bg-[#475569] text-white", dot: null, label: status || "—" };
  return (
    <span data-testid={testId} data-status={status}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-xs font-extrabold tracking-wide leading-none ${s.cls}`}>
      {s.dot ? <span className={`h-2 w-2 rounded-full ${s.dot}`} /> : <CheckCircle2 className="h-3.5 w-3.5" />}
      {s.label}
    </span>
  );
}

function Chip({ children, cls, title, testId }) {
  return (
    <span title={title} data-testid={testId}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold leading-none whitespace-nowrap ${cls}`}>{children}</span>
  );
}

/** Dense metric cell: small label above a readable value. */
function Metric({ label, value, cls, sub, testId }) {
  // Natural width, never truncated: the row wraps instead of hiding digits.
  return (
    <div className="shrink-0 whitespace-nowrap" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-[13px] mono font-semibold leading-tight ${cls || "text-slate-100"}`}>{value}</div>
      {sub && <div className="text-[10px] mono text-slate-500 leading-tight">{sub}</div>}
    </div>
  );
}

/** Pair counts for Pair Entry trades, read straight from pair_funding / pair_progress. Display only.
 *  open   = pairs currently held (pair_funding.completed_pairs)
 *  cap    = pairs the funding model admits (total_pairs_by_level at max_tradable_level)
 *  entered= all pairs filled this cycle (sum of per-level pairs_complete) — stays after exit */
function PairsMetric({ trade, testId }) {
  if (!trade.pair_execution_enabled) {
    return <Metric label="Pairs" value="—" sub="level entry" testId={testId} />;
  }
  const levels = trade.pair_progress?.levels || [];
  const entered = levels.reduce((a, l) => a + (l.pairs_complete || 0), 0);
  const open = trade.open_hedged_pairs ?? 0;
  const cap = trade.max_pairs_affordable;
  const inflight = trade.pair_progress?.in_flight;
  const blk = inflight?.entry_block;
  const sub = inflight
    ? `${blk?.state === "STALLED" ? "stalled" : blk ? "retrying" : "filling"} L${inflight.level_number ?? "?"} · ${inflight.pairs_complete ?? "?"}/${inflight.pairs_required ?? "?"}`
    : trade.status === "COMPLETE" ? `${entered} entered · all closed`
      : `${entered} entered${cap != null ? ` · cap ${cap}` : ""}`;
  return <Metric label="Pairs open" value={open} sub={sub} cls={open ? "text-slate-100" : "text-slate-400"} testId={testId} />;
}

const pnlCls = (v) => (v == null ? "text-slate-400" : v > 0 ? "text-[#00C805]" : v < 0 ? "text-[#FF6b62]" : "text-slate-200");

/** Compact card cell: small label above a readable value. `wrap` lets long contract symbols
 *  break instead of widening the row. Display only. */
function CardCell({ label, value, cls, testId, wrap, title }) {
  return (
    <div className={wrap ? "min-w-0 max-w-[10rem]" : "shrink-0 whitespace-nowrap"} data-testid={testId} title={title}>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 whitespace-nowrap">{label}</div>
      <div className={`text-[12px] mono font-semibold leading-tight ${wrap ? "break-words" : ""} ${cls || "text-slate-100"}`}>{value}</div>
    </div>
  );
}

/** Exact tradable symbol, allowed to wrap after the exchange prefix. */
function CardSymbol({ symbol }) {
  if (!symbol) return "—";
  const s = String(symbol);
  const i = s.indexOf(":");
  return i < 0 ? s : <>{s.slice(0, i + 1)}<wbr />{s.slice(i + 1)}</>;
}

/** Target % / ₹ on the card. The values are edited in the trade's settings (the same place as
 *  before); clicking the cell opens them. */
function TargetCell({ label, value, onEdit, disabled, testId }) {
  if (!onEdit) return <CardCell label={label} value={value} testId={testId} />;
  return (
    <button type="button" onClick={onEdit} disabled={disabled} data-testid={testId}
      title={disabled ? "Trade is COMPLETE" : "Edit target in trade settings"}
      className="shrink-0 whitespace-nowrap text-left rounded-sm px-1 -mx-1 hover:bg-[#1A1D24] disabled:hover:bg-transparent disabled:cursor-default">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label} {!disabled && <Settings className="inline h-2.5 w-2.5" />}</div>
      <div className="text-[12px] mono font-semibold leading-tight text-slate-100">{value}</div>
    </button>
  );
}

/** TAB 4 — TRADE DETAILS. Everything the compact trade card used to show that is not in its
 *  single operational row. Pure re-presentation of fields already on the trade view; the Gate A
 *  next-pair figures, trigger/distance, lots and entry limits stay in Next Execution. */
function DetailSection({ title, children, testId }) {
  return (
    <div className="term-panel rounded-sm p-2.5 mb-3" data-testid={testId}>
      <div className="text-[9px] uppercase tracking-wide text-slate-500 font-bold mb-2">{title}</div>
      <div className="flex flex-wrap gap-x-6 gap-y-2.5">{children}</div>
    </div>
  );
}

function TradeDetailsTab({ trade }) {
  const s = trade.slot;
  const tc = trade.tradable_capacity;
  const configured = tc?.configured_levels ?? (trade.ladder_plan || []).length ?? 10;
  const edgeOk = trade.edge != null && trade.edge_threshold != null ? trade.edge > trade.edge_threshold : null;
  return (
    <div data-testid={`trade-details-${s}`}>
      <DetailSection title="Status and next step">
        <div className="text-[12px] mono w-full" data-testid={`next-trigger-summary-${s}`}>
          <NextTriggerSummary trade={trade} />
        </div>
      </DetailSection>

      <DetailSection title="Spreads">
        <Metric label="Current / Running Spread" value={num(trade.current_spread, 4)} cls="text-slate-100" testId={`current-spread-${s}`} />
        <Metric label="Reference Spread" value={num(trade.reference_spread, 4)} />
        <Metric label="Actual Average Entry Spread" value={num(trade.actual_average_entry_spread, 4)} />
        <Metric label="Planned Final Average Spread" value={num(trade.planned_final_average_spread, 4)} />
      </DetailSection>

      <DetailSection title="P&L and target">
        <Metric label="Unrealised P&L" value={inr(trade.unrealised_pnl)} cls={pnlCls(trade.unrealised_pnl)} testId={`unrealised-${s}`} />
        <Metric label="Realised P&L" value={inr(trade.realised_pnl)} cls={pnlCls(trade.realised_pnl)} testId={`realised-${s}`} />
        <Metric label="Total P&L" value={inr(trade.combined_pnl)} cls={pnlCls(trade.combined_pnl)} />
        <Metric label="Target P&L ₹ Value" value={inr(trade.target_pnl)}
          sub={trade.target_percent != null ? `${num(trade.target_percent, 4)}% · ${trade.target_basis}` : "G24 target"} />
        <Metric label="Target source" value={trade.target_source || "—"} />
        <Metric label="Auto square-off" value={trade.auto_square_off ? "ON" : "OFF"}
          cls={trade.auto_square_off ? "text-[#00C805]" : "text-slate-400"} />
      </DetailSection>

      <DetailSection title="Strategy capital · Gate A">
        <Metric label="Zerodha Broker Available Funds" testId={`detail-broker-funds-${s}`}
          value={brokerFundsText(trade.broker_funds)} sub={brokerFundsSub(trade.broker_funds)}
          cls={trade.broker_funds?.available != null ? "text-[#5b9bff]" : "text-slate-500"} />
        <Metric label="Trade Allocated Capital" value={inr(trade.paper_allocated_capital ?? trade.allocated_funds)} />
        <Metric label="Trade Remaining Allocation" value={inr(trade.paper_available_funds ?? trade.funds_remaining)} />
        <Metric label="Capital Used / Allocated" testId={`capital-${s}`}
          value={`${inr(trade.paper_capital_used ?? trade.funds_used)} / ${inr(trade.paper_allocated_capital ?? trade.allocated_funds)}`}
          sub={`remaining ${inr(trade.paper_available_funds ?? trade.funds_remaining)}`} />
        <Metric label="Maximum Hedged Capital Utilisable"
          value={trade.max_hedged_capital_utilisable != null ? inr(trade.max_hedged_capital_utilisable) : "—"}
          sub={trade.max_pairs_affordable != null ? `${trade.max_pairs_affordable} pairs` : undefined} />
        <Metric label="Margin buffer" value={trade.margin_buffer_multiple != null ? `${num(trade.margin_buffer_multiple, 2)}×` : "—"} />
        <Metric label="Open hedged pairs" value={trade.open_hedged_pairs ?? "—"} />
        <Metric label="Edge / Thr" testId={`edge-${s}`}
          value={`${trade.edge != null ? (trade.edge * 100).toFixed(3) : "—"}% / ${trade.edge_threshold != null ? (trade.edge_threshold * 100).toFixed(3) : "—"}%`}
          cls={edgeOk === null ? "text-slate-300" : edgeOk ? "text-[#00C805]" : "text-[#FF6b62]"}
          sub={edgeOk === null ? undefined : edgeOk ? "edge gate passed" : "below K1 threshold"} />
      </DetailSection>

      {trade.auto_exit && (
        <DetailSection title="Conditional auto square-off" testId={`auto-exit-details-${s}`}>
          <Metric label="State" value={trade.auto_exit.state || "—"}
            cls={trade.auto_exit.state === "PAUSED" ? "text-[#FFCC00]" : "text-[#FF9500]"}
            sub={`sequence ${trade.auto_exit.sequence_no ?? "—"} · ${trade.auto_exit.orders_completed || 0} exit orders completed`} />
          <Metric label="Last re-check P&L" value={inr(trade.auto_exit.last_check?.remaining_book_pnl)}
            sub={`target ${inr(trade.auto_exit.last_check?.target_pnl)} · ${trade.auto_exit.last_check?.decision || "—"}`} />
          <Metric label="Open legs" value={trade.auto_exit.last_check?.open_legs ?? "—"}
            sub={trade.auto_exit.pending_order ? "waiting for the broker to confirm an exit" : undefined} />
          {trade.auto_exit.working_exit && (
            <Metric label="Working exit" value={trade.auto_exit.working_exit.mode === "FLOOR"
                ? `limit ${trade.auto_exit.working_exit.limit} · bound ${trade.auto_exit.working_exit.bound}`
                : `limit ${trade.auto_exit.working_exit.limit}`}
              sub={trade.auto_exit.working_exit.text} testId={`auto-exit-working-detail-${s}`} />
          )}
        </DetailSection>
      )}

      <DetailSection title="Levels and pairs">
        <Metric label="Levels" value={`${trade.completed_full_levels ?? trade.entered_levels ?? 0} complete${trade.current_partial_level
          ? ` + L${trade.current_partial_level} ${(trade.next_level_detail?.pairs_complete || 0) > 0 ? "partial" : "entering"} (${trade.next_level_detail?.pairs_complete ?? 0}/${trade.next_level_detail?.pairs_required ?? "?"})`
          : ""}`}
          sub={`${configured} configured`} testId={`levels-${s}`} />
        <PairsMetric trade={trade} testId={`pairs-${s}`} />
        <Metric label="Open levels" value={trade.open_levels ?? 0} />
        {trade.partial_pairs > 0 && (
          <Metric label="Partial pairs" value={trade.partial_pairs} cls="text-[#FFCC00]"
            sub="one leg exited, the other still open" testId={`partial-pairs-${s}`} />
        )}
        <Metric label="Execution" value={trade.pair_execution_enabled ? "PAIR ENTRY" : "LEVEL ENTRY"} />
        <Metric label="Ladder" value={trade.trade_trigger_pct != null ? `Trigger ${trade.trade_trigger_pct}%` : "Standard trigger ladder"}
          sub={trade.trade_trigger_pct != null ? `trigger ${trade.trade_trigger_pct}%` : undefined} />
      </DetailSection>

      <DetailSection title="Contracts">
        <Metric label="SELL leg" value={trade.sell_symbol || "—"} cls="text-[#FF6b62]" />
        <Metric label="BUY leg" value={trade.buy_symbol || "—"} cls="text-[#5b9bff]" />
        <Metric label="Direction" testId={`direction-${s}`}
          value={trade.direction === "SELL_FAR_BUY_NEAR" ? "SELL FAR / BUY NEAR" : trade.direction === "SELL_NEAR_BUY_FAR" ? "SELL NEAR / BUY FAR" : "—"} />
        {trade.spot_symbol && <Metric label="Spot (price input only)" value={trade.spot_symbol} cls="text-slate-400" />}
        <Metric label="DTE" value={trade.days_to_expiry ?? "—"} sub={trade.near_expiry ? `near expiry ${trade.near_expiry}` : undefined} />
        <Metric label="Qty / lot (P&L)" value={num(trade.pnl_multiplier ?? trade.qty_per_lot, 0)} />
        <Metric label="Broker qty / lot" value={trade.broker_qty_per_lot != null ? num(trade.broker_qty_per_lot, 0) : "—"} />
      </DetailSection>
    </div>
  );
}

// Card button styles, injected once so the card renders the same wherever it is used.
const TRADE_CARD_CSS = `
  .btn-primary-green{background:var(--gz-green);color:#fff}
  .btn-primary-green:hover:not(:disabled){background:var(--gz-green-hover)}
  .btn-neutral,.btn-secondary{background:var(--term-s2);color:var(--term-secondary);border-color:var(--term-border)}
  .btn-neutral:hover:not(:disabled),.btn-secondary:hover:not(:disabled){background:var(--term-hover);color:var(--term-primary);border-color:var(--term-border-strong)}
  .btn-sqoff-live{background:var(--gz-red);color:#fff}
  .btn-sqoff-live:hover:not(:disabled){background:var(--gz-red-hover)}
  .btn-chip{display:inline-flex;align-items:center;font-size:10px;font-weight:800;padding:3px 6px;border-radius:3px;border-width:1px;border-style:solid;line-height:1;white-space:nowrap}
  .btn-chip:disabled{cursor:default}
  .btn-chip:not(:disabled):hover{filter:brightness(1.2)}
  .menu-item{display:flex;align-items:center;gap:6px;width:100%;text-align:left;font-size:11px;font-weight:600;padding:6px 10px;color:var(--term-primary)}
  .menu-item:hover:not(:disabled){background:var(--term-s3)}
  .menu-item:disabled{opacity:.35;cursor:not-allowed}
`;
function useTradeCardStyles() {
  useEffect(() => {
    if (document.getElementById("gz-trade-card-css")) return;
    const el = document.createElement("style");
    el.id = "gz-trade-card-css";
    el.textContent = TRADE_CARD_CSS;
    document.head.appendChild(el);
  }, []);
}

/** Plan › Additional Information — the target, charges and P&L figures behind the AUTO decision.
 *  AUTO fires when Net P&L at LTP (gross - charges incurred - estimated exit charges) >= the target (27 Oct 2026). */
function AdditionalInfoTab({ trade }) {
  const s = trade.slot;
  const ch = trade.charges || {};
  const comp = ch.components || {};
  const eff = trade.effective_target;
  const gross = trade.cumulative_gross_pnl ?? trade.combined_pnl;
  const eligible = eff != null && gross != null ? gross > eff : null;
  return (
    <div data-testid={`additional-info-${s}`}>
      <DetailSection title="Target and charges" testId={`info-target-${s}`}>
        <Metric label="Base Target" value={inr(trade.base_target ?? trade.target_pnl)} testId={`info-base-target-${s}`}
          sub={trade.target_percent != null ? `${num(trade.target_percent, 4)}% · ${trade.target_basis}` : trade.target_basis} />
        <Metric label="Charges Already Incurred" value={inr(trade.charges_incurred)} testId={`info-charges-incurred-${s}`}
          sub={ch.model === "ZERO" ? "charges model off" : `rate v${ch.rate_version ?? "—"} · brokerage ${inr(comp.brokerage)} · STT/CTT ${inr(comp.stt_ctt)} · GST ${inr(comp.gst)}`} />
        <Metric label="Estimated Remaining Exit Charges" value={inr(trade.est_remaining_exit_charges)} testId={`info-charges-remaining-${s}`}
          sub={`${ch.remaining_exit_orders ?? 0} exit orders at current LTP`} />
        <Metric label="Maximum Estimated Brokerage" value={inr(trade.est_remaining_brokerage_max)}
          sub="remaining exit orders × ₹20 cap" />
        <Metric label="Estimated statutory / transaction" value={inr(trade.est_remaining_statutory)}
          sub="STT/CTT, exchange, SEBI, stamp, GST — estimates" />
        <Metric label="Effective Target" value={eff != null ? inr(eff) : "—"} testId={`info-effective-target-${s}`}
          cls="text-slate-100"
          sub={eff == null ? (ch.available === false ? "charges unavailable — AUTO will not fire" : "target unavailable") : "the same bar on the gross mark: target + incurred + estimated exit"} />
        {trade.target_capital_base != null && <Metric label="Target capital base" value={inr(trade.target_capital_base)} testId={`info-target-base-${s}`}
          sub={trade.target_includes_charges ? "capital used + entry charges + estimated exit charges" : "capital used (trade created before 27 Oct 2026)"} />}
      </DetailSection>
      <DetailSection title="P&L (gross)" testId={`info-pnl-${s}`}>
        <Metric label="Previous / Carried Realised P&L" value={inr(trade.carried_realised_pnl)} cls={pnlCls(trade.carried_realised_pnl)}
          testId={`info-carried-${s}`} sub="from earlier contract sets (rollover)" />
        <Metric label="Current Trade / Rollover P&L" value={inr(trade.current_rollover_pnl)} cls={pnlCls(trade.current_rollover_pnl)}
          testId={`info-current-${s}`} />
        <Metric label="Cumulative Gross P&L" value={inr(gross)} cls={pnlCls(gross)} testId={`info-cumulative-${s}`}
          sub={eligible == null ? undefined : eligible ? "above the effective target" : "not above the effective target"} />
        <Metric label="Net P&L at LTP" value={inr(trade.net_pnl_ltp)} cls={pnlCls(trade.net_pnl_ltp)} testId={`info-net-ltp-${s}`}
          sub="gross − charges incurred − estimated exit charges · what AUTO compares with the target" />
        <Metric label="Net P&L at bid/ask" value={inr(trade.net_pnl_bidask)} cls={pnlCls(trade.net_pnl_bidask)} testId={`info-net-bidask-${s}`}
          sub="long legs at the bid, short legs at the ask, after the same charges" />
      </DetailSection>
      <DetailSection title="Contracts">
        <Metric label="Pinned NEAR" value={trade.near_symbol || "—"} sub={trade.near_expiry ? `expiry ${trade.near_expiry}` : undefined} />
        <Metric label="Pinned FAR" value={trade.far_symbol || "—"} />
        <Metric label="DTE (earlier leg)" value={trade.days_to_expiry ?? "—"} />
        {trade.entry_gate?.reason && <Metric label="Entry gate" value={trade.entry_gate.reason} cls="text-[#FFCC00]" />}
        <Metric label="Contract set" value={trade.current_set ?? 0} sub={`${(trade.rollover_history || []).length} rollover(s) completed`} />
      </DetailSection>
      {(trade.rollover?.state && trade.rollover.state !== "NONE") && (
        <DetailSection title="Rollover" testId={`info-rollover-${s}`}>
          <Metric label="State" value={trade.rollover.state} sub={rolloverWaitReason(trade.rollover) || undefined} />
          <Metric label="Pairs rolled" value={`${trade.rollover.pairs_done || 0} / ${trade.rollover.pairs_total || 0}`} />
          <Metric label="Minimum spread" value={num(trade.rollover.min_spread, 4)} sub="frozen Average Spread (SELL − BUY)" />
        </DetailSection>
      )}
    </div>
  );
}

/** ROLL OVER confirmation: the old and new contract pairs, the direction, the minimum spread
 *  (the trade's frozen Average Spread) and how many pairs will move. Nothing is sent until the user
 *  confirms; the backend also requires {confirm: true, environment}. Pairs then roll one at a
 *  time, only while the new pair's spread is at least the minimum. */
export function RolloverDialog({ tradeId, tradeName, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/paper/v25/trades/${tradeId}/rollover-preview`)
      .then((r) => { if (live) setPreview(r.data); })
      .catch((e) => { if (live) setError(detailText(e, "Could not load the rollover preview")); });
    return () => { live = false; };
  }, [tradeId]);
  const plan = preview?.plan;
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/paper/v25/trades/${tradeId}/rollover`, { confirm: true, environment: preview?.environment });
      toast.success(`Rollover started for ${preview?.name || tradeName}`);
      onDone?.();
      onClose();
    } catch (e) { setError(detailText(e, "Rollover could not start")); }
    setBusy(false);
  };
  const dir = (d) => (d === "SELL_FAR_BUY_NEAR" ? "SELL FAR / BUY NEAR" : d === "SELL_NEAR_BUY_FAR" ? "SELL NEAR / BUY FAR" : "—");
  return (
    <TerminalDrawer title={`Roll Over · ${preview?.name || tradeName || ""}`} onClose={onClose} width={520}
      testId="rollover-dialog" busy={busy}>
      {!preview && !error && <div className="text-xs text-slate-500">Loading the rollover plan…</div>}
      {preview && (
        <div className="space-y-3 text-xs">
          <div className="text-sm font-bold text-slate-100">Are you sure?</div>
          {preview.environment === "LIVE" && (
            <div className="rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/15 p-2 text-[#FF6b62] font-bold">
              LIVE — REAL broker orders will be placed on Zerodha.
            </div>
          )}
          {!preview.available && (
            <div role="alert" className="rounded-sm border border-[#FFCC00]/40 bg-[#FFCC00]/10 p-2 text-[#FFCC00]" data-testid="rollover-unavailable">
              Not available: {(preview.reasons || []).join(", ")}
            </div>
          )}
          {plan && (
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 mono" data-testid="rollover-plan">
              <span className="text-slate-500">Old pair</span><span>{plan.old.near} + {plan.old.far}</span>
              <span className="text-slate-500">Old direction</span><span>{dir(plan.old.direction)}</span>
              <span className="text-slate-500">Earlier leg DTE</span><span>{plan.old.earlier_leg_dte ?? "—"}</span>
              <span className="text-slate-500">New pair</span><span data-testid="rollover-new-pair">{plan.new.near} + {plan.new.far}</span>
              <span className="text-slate-500">New direction</span>
              <span className={plan.direction_changes ? "text-[#FFCC00] font-bold" : ""}>{dir(plan.new.direction)}{plan.direction_changes ? " (changes)" : ""}</span>
              <span className="text-slate-500">Minimum spread</span><span data-testid="rollover-min-spread">{num(plan.min_spread, 4)} (Average Spread)</span>
              <span className="text-slate-500">New pair spread now</span><span>{num(plan.candidate_spread, 4)}</span>
              <span className="text-slate-500">Pairs to roll</span><span>{plan.pairs_total}</span>
            </div>
          )}
          <div className="rounded-sm border border-[#2A2F3A] p-2 text-slate-400">
            Pair by pair: the old legs are closed, then the new ones opened; a pair rolls only while the new
            spread (SELL − BUY) is at least the minimum, otherwise the rollover waits. Where the Mid contract is
            already held on the right side it is kept and only the other legs trade.
          </div>
        </div>
      )}
      {error && <div role="alert" className="mt-3 text-xs text-[#FF6b62]">{error}</div>}
      <div className="terminal-drawer-footer">
        <button onClick={onClose} disabled={busy} className="btn-x bg-[#232730] border border-[#2A2F3A]" data-testid="rollover-cancel">Cancel</button>
        <button onClick={confirm} disabled={busy || !preview?.available} className="btn-x bg-[#7c3aed]" data-testid="rollover-confirm">
          {busy ? "Starting…" : "Confirm Roll Over"}
        </button>
      </div>
    </TerminalDrawer>
  );
}

/** FORCE SQUARE-OFF confirmation. Nothing is sent until the user confirms; the backend also
 * refuses the request without {confirm: true, environment}. After confirmation the square-off is
 * unconditional (target logic ignored) and continues until flat — each exit leg counts as closed
 * only when its own order reaches COMPLETE. */
export function ConfirmSquareOffDialog({ tradeId, tradeName, onClose, onDone }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let live = true;
    api.get(`/paper/v25/trades/${tradeId}/square-off-preview`)
      .then((r) => { if (live) setPreview(r.data); })
      .catch((e) => { if (live) setError(detailText(e, "Could not load the square-off preview")); });
    return () => { live = false; };
  }, [tradeId]);
  const env = preview?.environment;
  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/paper/v25/trades/${tradeId}/square-off`, { confirm: true, environment: env });
      toast.warning(`Force square-off started for ${preview?.name || tradeName}`);
      onDone?.();
      onClose();
    } catch (e) {
      setError(detailText(e, "Square-off failed"));
    }
    setBusy(false);
  };
  return (
    <TerminalDrawer title={`Force Square-Off · ${preview?.name || tradeName || ""}`} onClose={onClose} width={480}
      testId="sqoff-confirm-dialog" busy={busy}>
      {!preview && !error && <div className="text-xs text-slate-500">Loading what will be exited…</div>}
      {preview && (
        <div className="space-y-3 text-xs">
          <div className="text-sm font-bold text-slate-100">Are you sure?</div>
          {env === "LIVE" && (
            <div className="rounded-sm border border-[#FF3B30]/60 bg-[#FF3B30]/15 p-2 text-[#FF6b62] font-bold" data-testid="sqoff-live-warning">
              LIVE — REAL broker orders will be placed on Zerodha with real capital.
            </div>
          )}
          {env === "SHADOW" && (
            <div className="rounded-sm border border-[#FFCC00]/50 bg-[#FFCC00]/10 p-2 text-[#FFCC00]" data-testid="sqoff-shadow-warning">
              SHADOW LIVE — real orders will be sent to the validation account.
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 mono" data-testid="sqoff-preview">
            <span className="text-slate-500">Trade</span><span>{preview.name}</span>
            <span className="text-slate-500">Environment</span><span data-testid="sqoff-env">{env}</span>
            <span className="text-slate-500">Underlying</span><span>{preview.underlying || "—"}</span>
            <span className="text-slate-500">Open levels</span><span data-testid="sqoff-levels">{preview.open_levels}</span>
            <span className="text-slate-500">Hedged pairs</span><span data-testid="sqoff-pairs">{preview.hedged_pairs}</span>
            <span className="text-slate-500">Partial pairs</span><span>{preview.partial_pairs}</span>
            <span className="text-slate-500">Open legs</span><span data-testid="sqoff-legs">{preview.open_legs}</span>
            {preview.working_orders > 0 && <><span className="text-slate-500">Working orders</span><span>{preview.working_orders}</span></>}
          </div>
          <div className="rounded-sm border border-[#FF9500]/40 bg-[#FF9500]/10 p-2 text-[#FF9500]">
            Target logic will be ignored. This is unconditional: every current leg is exited until the trade is flat.
            A leg counts as closed only when its exit order is COMPLETE.
          </div>
        </div>
      )}
      {error && <div role="alert" className="mt-3 text-xs text-[#FF6b62]">{error}</div>}
      <div className="terminal-drawer-footer">
        <button onClick={onClose} disabled={busy} className="btn-x bg-[#232730] border border-[#2A2F3A]" data-testid="sqoff-cancel">Cancel</button>
        <button onClick={confirm} disabled={busy || !preview} className="btn-x btn-sqoff-live" data-testid="sqoff-confirm">
          {busy ? "Starting…" : "Confirm Square-Off"}
        </button>
      </div>
    </TerminalDrawer>
  );
}

/** One labelled column of the trade card. */
function CardSection({ title, children, testId }) {
  return <KitSection title={title} testId={testId}>{children}</KitSection>;
}

/** Label : value line inside a card section. */
function KV({ label, value, cls, testId, title }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12px] leading-tight" data-testid={testId} title={title}>
      <span className="text-fg-secondary whitespace-nowrap">{label}</span>
      <span className={`mono font-semibold text-right min-w-0 ${cls || "text-fg"}`}>{value}</span>
    </div>
  );
}

const shortTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const PAIR_LABEL = { NEAR_MID: "Near+Mid", MID_FAR: "Mid+Far" };
const ENV_STATUS = { LIVE: "LIVE", SHADOW: "SHADOW_LIVE", PAPER: "PAPER" };
const ENV_TITLE = {
  LIVE: "LIVE · orders are routed to Zerodha",
  SHADOW: "SHADOW LIVE / SIMULATED STRATEGY FILL / REAL BROKER REJECTION · real Zerodha orders are sent and expected to be refused; the strategy then advances on a simulated fill at the intended limit price",
  PAPER: "PAPER_BROKER · live ZERODHA market data, simulated fills",
};

/** Risk / data warnings, derived only from fields the backend already reports. */
export function tradeWarnings(trade, openPos) {
  const w = [];
  const skipped = trade.last_exit_check?.skipped_reason;
  const rowStatus = trade.current_row?.market_data_status;
  if (skipped === "MARKET_DATA_STALE" || skipped === "ROW_NOT_OK" || (rowStatus && rowStatus !== "OK")) {
    w.push({ status: "MARKET_DATA_STALE", title: `Engine skipped its last check: ${skipped || rowStatus}` });
  }
  if (trade.charges && trade.charges.available === false) {
    w.push({ status: "CHARGES_UNAVAILABLE", title: "Charges could not be calculated — AUTO does not compare P&L without an effective target" });
  }
  if ((trade.partial_pairs || 0) > 0) {
    w.push({ status: "PARTIAL_PAIR", label: `PARTIAL PAIR × ${trade.partial_pairs}`, title: "A pair holds one leg only" });
  }
  const gate = trade.entry_gate?.reason;
  if (gate && /HOURS|SESSION|CLOSED/i.test(gate)) w.push({ status: "OUTSIDE_HOURS", title: gate });
  if (trade.recovery_state === "RECOVERY_PENDING") {
    w.push({ status: "RECOVERY_PENDING", title: (trade.recovery_reasons || []).join(", ") || trade.recovery_state });
  }
  if (openPos && trade.reconciliation_state && trade.reconciliation_state !== "RECONCILED") {
    w.push({ status: "NOT_RECONCILED", label: `NOT RECONCILED · ${trade.reconciliation_state}` });
  }
  return w;
}

export function TradeCard({ trade, onChange, onOpenPlan, onEditSettings, onRemove, forceOpen, duplicateName }) {
  const [busy, setBusy] = useState(false);
  // 30 Oct 2026: the card stays compact on every page; the full trade (Contracts · Economics · Execution · Risk & State)
  // opens in the Details modal. A trade reached from a link (forceOpen) opens with its details showing.
  const [expanded, setExpanded] = useState(() => !!forceOpen);
  useEffect(() => { if (forceOpen) setExpanded(true); }, [forceOpen]);
  // Header click opens the details, except on its own controls (buttons, links, inputs).
  const onHeaderClick = (e) => { if (!e.target.closest("button, a, input, select, [data-disabled-reason]")) setExpanded(true); };
  const [confirmSqOff, setConfirmSqOff] = useState(false);
  const [rollOpen, setRollOpen] = useState(false);
  const [analyse, setAnalyse] = useState(false);
  const ref = useRef(null);
  useTradeCardStyles();

  useEffect(() => {
    if (forceOpen) ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [forceOpen]);

  const act = async (path, body) => {
    setBusy(true);
    try { await api.post(`/paper/v25/trades/${trade.id}/${path}`, body || {}); onChange(); }
    catch (e) { toast.error(detailText(e, `${path} failed`)); }
    setBusy(false);
  };

  const st = trade.status;
  const s = trade.slot;
  const pairOn = !!trade.pair_execution_enabled;
  // Exposure, not just OPEN levels: an ENTERING level with filled pairs, a partial pair, or a
  // square-off still in progress must all be squarable from the card.
  const openPos = (trade.open_levels || 0) > 0 || (trade.exposed_levels || 0) > 0
    || (trade.partial_pairs || 0) > 0 || trade.status === "EXITING";
  const canStart = !busy && !!trade.assigned_commodity && !["RUNNING", "EXITING", "RESETTING", "COMPLETE"].includes(st);
  const canStop = !busy && st === "RUNNING";
  const canSquareOff = !busy && openPos;
  const canReset = !busy && st !== "COMPLETE";
  // Phase 0 rollover (engine unchanged). The button is shown on every running pair trade with a
  // position; it is enabled once the earlier leg is below the backend's rollover threshold.
  const ro = trade.rollover || {};
  const rolling = ["ROLLING", "ROLL_PAUSED", "ROLL_STOPPED"].includes(ro.state);
  const canResumeRoll = ro.state === "ROLL_STOPPED" || (ro.state === "ABORTED_BY_EXIT" && st === "RUNNING");
  const rollLimit = trade.rollover_dte_below ?? 10;
  const rollShown = !rolling && openPos && pairOn && st === "RUNNING";
  const rollOffer = rollShown && trade.earlier_leg_dte != null && trade.earlier_leg_dte < rollLimit;
  const expiryWarn = openPos && trade.earlier_leg_dte != null && trade.earlier_leg_dte <= 2;
  const canToggleMode = !busy && st !== "RUNNING" && st !== "EXITING" && st !== "COMPLETE";
  const market = trade.market_segment === "NFO" ? "NFO" : "MCX";
  const env = ENV_STATUS[trade.environment] ? trade.environment : "PAPER";

  const flipMode = async () => {
    setBusy(true);
    try {
      await api.post(`/paper/v25/trades/${trade.id}/pair-execution`, { enabled: !pairOn });
      toast.success(`${!pairOn ? "Pair" : "Level"} entry selected for ${trade.assigned_commodity || trade.name}`);
      onChange();
    } catch (e) { toast.error(detailText(e, "Could not change execution mode")); }
    setBusy(false);
  };

  // Live opportunity row for the assigned contracts — the same backend row the engine reads.
  const row = trade.current_row || {};
  const hasRow = !!trade.assigned_commodity && !!trade.current_row;
  const pct = (v, dp) => (v == null || !hasRow ? "—" : `${(Number(v) * 100).toFixed(dp)}%`);
  // Every money figure below is the backend's own value — nothing is recalculated here.
  const pnl = trade.cumulative_gross_pnl ?? trade.combined_pnl;
  const effTarget = trade.effective_target;
  const net = futuresNet(trade);                  // NET at LTP (the AUTO target basis) and NET at bid/ask
  const netTarget = trade.target_pnl;
  const baseTarget = trade.base_target ?? trade.target_pnl;
  const chargesOk = !(trade.charges && trade.charges.available === false);
  // Display ratio of two backend values (bar width only).
  const progress = pnl != null && effTarget > 0 ? Math.max(0, Math.min(100, (pnl / effTarget) * 100)) : null;
  const netProgress = net.ltp != null && netTarget > 0 ? Math.max(0, Math.min(100, (net.ltp / netTarget) * 100)) : null;
  const peak = trade.peak_pnl && (trade.execution_attempt == null || trade.peak_pnl.attempt === trade.execution_attempt)
    ? trade.peak_pnl : null;
  const warnings = tradeWarnings(trade, openPos);
  const stale = warnings.some((w) => w.status === "MARKET_DATA_STALE");
  // ROLLED only once a pair actually moved onto a later contract set (the backend makes the new
  // set current on the first rolled pair). A prepared, paused or aborted rollover is not a roll.
  const rolled = (trade.current_set || 0) > 0;
  const rollAbortedEmpty = ro.state === "ABORTED_BY_EXIT" && !(ro.pairs_done > 0);
  const rollNow = rolloverStatus(trade, { offered: rollOffer });
  // the Common Trade Panel's spread bar: entry spread, spread now, next trigger (lib/commonTrades futuresRow)
  const scaleRow = futuresRow(trade);

  const startReason = !trade.assigned_commodity ? "Assign an opportunity first"
    : st === "COMPLETE" ? "Trade is COMPLETE — remove it and create a new trade"
      : ["RUNNING", "EXITING", "RESETTING"].includes(st) ? `Already ${st}` : "Working…";
  const rollReason = `Available when the earlier leg is below ${rollLimit} DTE (now ${trade.earlier_leg_dte ?? "unknown"})`;

  return (
    <CardFrame innerRef={ref} testId={`trade-card-${s}`} dataStatus={st} highlight={forceOpen}
      tone={st === "RUNNING" ? "good" : st === "EXITING" || st === "RESETTING" ? "warn" : st === "COMPLETE" ? "done" : st === "READY" ? "busy" : "bad"}
      danger={stale || expiryWarn}>

      {/* ── A. Identity + state badges ── */}
      <header className="flex items-center gap-x-2 gap-y-1 flex-wrap cursor-pointer select-none -mx-1 px-1 rounded-sm hover:bg-hover"
        onClick={onHeaderClick} data-testid={`card-header-${s}`}>
        <button type="button" onClick={() => setExpanded(true)} aria-expanded={expanded}
          aria-controls={`card-body-${s}`} data-testid={`card-toggle-${s}`}
          title="Open full trade details"
          className="inline-flex h-6 w-6 items-center justify-center rounded border border-[var(--term-border)] text-fg-secondary hover:bg-hover hover:text-fg">
          <Maximize2 className="h-3 w-3" aria-hidden="true" />
          <span className="sr-only">Details of {trade.name}</span>
        </button>
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm whitespace-nowrap bg-[#00C805]/15 text-[var(--success-fg)]" data-testid={`strategy-${s}`}>FUTURES SPREAD</span>
        <span className="text-sm font-bold text-fg whitespace-nowrap">
          {trade.name}{duplicateName && <span className="text-[10px] font-normal text-fg-secondary mono" title={`Trade id ${trade.id}`}> #{trade.id.slice(0, 4)}</span>}
        </span>
        {trade.assigned_commodity && <span className="text-xs mono text-fg-secondary whitespace-nowrap" data-testid={`c-underlying-${s}`}>{trade.assigned_commodity}</span>}
        <ModePill env={env} />
        <StatusPill status={st} testId={`status-${s}`} />
        {/* The execution environment is a property of the trade, not of this page: a LIVE trade
            sends its orders to Zerodha, a PAPER trade fills them internally on the same live
            market data. They are separate instances and never share state. */}
        <StatusBadge status={ENV_STATUS[env]} title={ENV_TITLE[env]} data-testid={`env-badge-${s}`} />
        <StatusBadge label={market} tone={market === "NFO" ? "info" : "neutral"} icon={null} data-testid={`market-badge-${s}`} />
        <span data-testid="pair-execution-toggle" className="inline-flex">
          <button type="button" onClick={flipMode} disabled={!canToggleMode} data-testid="pair-execution-btn"
            title={canToggleMode ? `Switch to ${pairOn ? "LEVEL" : "PAIR"} entry` : "Execution mode can only change while the trade is stopped"}
            className={`btn-chip border ${pairOn ? "bg-[#7c3aed]/15 text-[#6d28d9] dark:text-[#c4b5fd] border-[#7c3aed]/50" : "bg-neutral-bg text-neutral-fg border-line"}`}>
            {pairOn ? "PAIR ENTRY" : "LEVEL ENTRY"}
          </button>
        </span>
        {trade.assigned_commodity && (
          <StatusBadge label={PAIR_LABEL[trade.pair_selection] || PAIR_LABEL.NEAR_MID} icon={null} data-testid={`pair-selection-${s}`} title="Contract pair selection" />
        )}
        {trade.current_set != null && (
          <StatusBadge label={`SET ${trade.current_set}${rolled ? " · ROLLED" : rollAbortedEmpty ? " · ROLLOVER ABORTED (0 PAIRS)" : ""}`} tone={rolled ? "info" : "neutral"} icon={null}
            data-testid={`contract-set-${s}`} title="Current contract set" />
        )}
        {trade.trade_trigger_pct != null && <StatusBadge label={`TRIGGER ${trade.trade_trigger_pct}%`} icon={null} title="Ladder trigger %" />}
        <StatusBadge status={trade.auto_square_off ? "AUTO_ON" : "AUTO_OFF"} data-testid={`auto-state-${s}`} />
        {/* Conditional AUTO square-off (one exit at a time, re-checked after each fill). */}
        {trade.auto_exit && (
          <StatusBadge data-testid={`auto-exit-${s}`}
            status={trade.auto_exit.state === "PAUSED" ? "AUTO_EXIT_PAUSED" : "AUTO_EXIT_ACTIVE"}
            label={`AUTO EXIT ${trade.auto_exit.state}${trade.auto_exit.orders_completed ? ` · ${trade.auto_exit.orders_completed} out` : ""}`}
            title={trade.auto_exit.state === "PAUSED"
              ? "Auto square-off paused: remaining P&L is not above target. Entries stay blocked; it resumes when P&L is above target again. Switch Auto Sq OFF to return to normal trading, or Force Square-Off to exit everything."
              : "Auto square-off in progress: one exit order at a time, P&L re-checked after each fill"} />
        )}
        {rolling && <RolloverStatus trade={trade} testId={`rollover-chip-${s}`} compact />}
        {expiryWarn && (
          <StatusBadge status="PINNED_UNAVAILABLE" tone="danger" label={`EXPIRY · DTE ${trade.earlier_leg_dte}`} data-testid={`expiry-warn-${s}`}
            title="The position still holds a contract near expiry and the rollover has not completed. No automatic roll below the minimum spread — roll, wait, or Force Square-Off." />
        )}
        {warnings.map((w) => (
          <StatusBadge key={w.status} status={w.status} label={w.label} title={w.title} data-testid={`warn-${w.status.toLowerCase()}-${s}`} />
        ))}
        {/* A square-off in progress is shown here, not only in Plan: it is the one state an
            operator must never miss. Everything else about the next step lives in Plan. */}
        {st === "EXITING" && (
          <span className="text-[11px] mono" data-testid={`exit-state-${s}`}><NextTriggerSummary trade={trade} /></span>
        )}
      </header>

      {trade.trigger_explain && (
        <div className="px-3 py-1 border-b border-line" data-testid={`trigger-explain-card-${s}`}>
          <TriggerExplain te={trade.trigger_explain} testId={`trigger-explain-${s}`} />
        </div>
      )}
      {trade.auto_exit?.working_exit && (
        <div className={`px-3 py-1 border-b border-line mono text-[11px] ${trade.auto_exit.working_exit.mode === "FLOOR" ? "text-[#FF6B60]" : "text-[#FF9500]"}`}
             data-testid={`auto-exit-working-${s}`}>
          ⚠ {trade.auto_exit.working_exit.text}
          {trade.auto_exit.working_exit.since ? ` · since ${new Date(trade.auto_exit.working_exit.since).toLocaleString()}` : ""}
        </div>
      )}

      {/* ── B–D. Contracts · Economics · Execution · Risk/State ── */}
      {(
        <CompactSummary testId={`card-compact-${s}`} scale={<CardScale row={scaleRow} />}
          pnl={<NetPnlPair ltp={net.ltp} bidask={net.bidask} finished={st === "COMPLETE"} testId={`c-pnl-${s}`}
            bidaskNote="long legs at the bid, short legs at the ask" dim={stale} at={stale ? "MARKET DATA STALE · last known" : null} />}
          risk={{ label: "Target (net)", testId: `c-eff-target-${s}`, value: chargesOk && netTarget != null ? inr(netTarget) : "—",
            sub: netTarget > 0 && net.ltp != null && st !== "COMPLETE" ? `progress ${progressText((net.ltp / netTarget) * 100, stale ? "MARKET_CLOSED" : "LIVE")}` : null,
            title: trade.target_capital_base != null
              ? `${trade.target_percent}% of capital used${trade.target_includes_charges ? " + charges" : ""} (${inr(trade.target_capital_base)}) — AUTO starts when Net P&L at LTP reaches it`
              : "AUTO starts when Net P&L at LTP reaches this target" }}
          size={{ label: "Levels", value: `${trade.entered_levels ?? 0} entered`, sub: pairOn ? `${trade.open_hedged_pairs ?? 0} pairs open` : "level entry" }}
          entry={{ label: "Avg entry spread", value: num(trade.actual_average_entry_spread, 4), sub: `now ${num(trade.current_spread, 4)}` }}
          data={{ label: "Market data", value: stale ? "STALE" : hasRow ? "OK" : "—", cls: stale ? "text-danger-fg" : "",
            sub: <><span className="text-danger-fg" data-testid={`c-sell-${s}`}>S <CardSymbol symbol={trade.sell_symbol} /></span> · <span className="text-info-fg" data-testid={`c-buy-${s}`}>B <CardSymbol symbol={trade.buy_symbol} /></span></> }} />
      )}
      <TradeDetailsModal open={expanded} onClose={() => setExpanded(false)} testId={`card-details-modal-${s}`}
        title={`${trade.name} · FUTURES SPREAD`} sub={`${trade.assigned_commodity || "no opportunity assigned"} · ${trade.sell_symbol || ""} / ${trade.buy_symbol || ""} · ${env} · ${market} · ${st}`}
        overview={<>
          <CardScale row={scaleRow} />
          <span className="text-xs">Spread now <b className="mono">{num(trade.current_spread, 4)}</b> · avg entry <b className="mono">{num(trade.actual_average_entry_spread, 4)}</b></span>
          <span className="text-xs">Levels <b className="mono">{trade.entered_levels ?? 0} entered</b></span>
          <span className="text-xs">Target (net) <b className="mono">{chargesOk && netTarget != null ? inr(netTarget) : "—"}</b></span>
          <span className="text-xs">Auto Sq <b>{trade.auto_square_off ? "ON" : "OFF"}</b></span>
        </>}>
      <div id={`card-body-${s}`}><CardSections testId={`card-row-${s}`}>
        <CardSection title="Contracts" testId={`sec-contracts-${s}`}>
          <KV label={market === "NFO" ? "Underlying" : "Commodity"} value={trade.assigned_commodity || "—"} testId={`commodity-${s}`} />
          {trade.assigned_commodity ? (
            <>
              <KV label="SELL" value={<CardSymbol symbol={trade.sell_symbol} />} cls="text-danger-fg break-words" testId={`sell-symbol-${s}`} />
              <KV label="BUY" value={<CardSymbol symbol={trade.buy_symbol} />} cls="text-info-fg break-words" testId={`buy-symbol-${s}`} />
            </>
          ) : <KV label="Contracts" value="No opportunity assigned" cls="text-fg-secondary" />}
          <KV label="Earlier-leg DTE" testId={`dte-${s}`}
            value={trade.earlier_leg_dte != null ? `${trade.earlier_leg_dte}d · ${rollOffer ? "roll eligible" : `roll below ${rollLimit}d`}` : "—"}
            cls={expiryWarn ? "text-danger-fg" : rollOffer ? "text-info-fg" : "text-fg"} />
          <div className="flex flex-wrap gap-x-4 gap-y-1 pt-0.5">
            {market === "NFO" && <CardCell label="Spot LTP" value={num(row.spot_ltp, 2)} testId={`spot-ltp-${s}`} />}
            <CardCell label="Near LTP" value={num(row.near_ltp, 2)} testId={`near-ltp-${s}`} />
            <CardCell label="Far LTP" value={num(row.far_ltp, 2)} testId={`far-ltp-${s}`} />
          </div>
        </CardSection>

        <CardSection title="Economics" testId={`sec-economics-${s}`}>
          <div className="flex items-end justify-between gap-3">
            <div data-testid={`pnl-${s}`}>
              <NetPnlPair ltp={net.ltp} bidask={net.bidask} finished={st === "COMPLETE"} testId={`net-${s}`}
                bidaskNote="long legs at the bid, short legs at the ask" dim={stale} />
              <div className="text-[10px] text-fg-secondary mt-0.5" title="Cumulative gross P&L at LTP, before charges (realised + unrealised, across rollovers)">
                gross {inr(pnl)} before charges</div>
            </div>
            <div className="text-right" data-testid={`effective-target-${s}`}
              title={`What AUTO compares Net P&L at LTP with${trade.target_capital_base != null ? ` — ${num(trade.target_percent, 4)}% of ${trade.target_basis} ${inr(trade.target_capital_base)}` : ""}`}>
              <div className="text-[10px] uppercase tracking-wide text-fg-secondary">Target (net)</div>
              <div className="text-sm mono font-bold text-fg leading-none">{chargesOk && netTarget != null ? inr(netTarget) : "—"}</div>
              {trade.target_capital_base != null && <div className="text-[10px] text-fg-secondary">{num(trade.target_percent, 4)}% of {inr(trade.target_capital_base)}</div>}
            </div>
          </div>
          {netProgress != null && (
            <div className="h-1.5 rounded bg-surface-muted overflow-hidden" role="progressbar" aria-label="Net P&L at LTP towards the target"
              aria-valuenow={Math.round(netProgress)} aria-valuemin={0} aria-valuemax={100}>
              <div className={`h-full ${netProgress >= 100 ? "bg-[var(--success-fg)]" : "bg-[var(--info-fg)]"}`} style={{ width: `${netProgress}%` }} />
            </div>
          )}
          <div className="text-[11px] mono text-fg-secondary" data-testid={`charges-line-${s}`}>
            Base {inr(baseTarget)} · Charges {chargesOk ? `${inr(trade.charges_incurred)} incurred · ~${inr(trade.est_remaining_exit_charges)} exit` : "unavailable"}
          </div>
          <div className="text-[11px] mono" data-testid={`peak-pnl-${s}`}>
            <span className="text-fg-secondary">Peak </span>
            <span className="text-success-fg" title="Peak positive P&L this cycle (engine-recorded)">
              {peak && peak.max > 0 ? `+${inr(peak.max)}${peak.max_at ? ` @${shortTime(peak.max_at)}` : ""}` : "+ —"}
            </span>
            <span className="text-fg-secondary"> / </span>
            <span className="text-danger-fg" title="Peak negative P&L this cycle (engine-recorded)">
              {peak && peak.min < 0 ? `${inr(peak.min)}${peak.min_at ? ` @${shortTime(peak.min_at)}` : ""}` : "− —"}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            <TargetCell label="Target %" testId={`target-pct-${s}`} onEdit={onEditSettings} disabled={st === "COMPLETE"}
              value={trade.target_percent != null ? `${num(trade.target_percent, 4)}%` : "—"} />
            <TargetCell label="Base target (₹)" testId={`target-inr-${s}`} onEdit={onEditSettings} disabled={st === "COMPLETE"}
              value={inr(trade.target_pnl)} />
            <CardCell label="Exp.P&L (J)" value={hasRow ? inr(row.expected_profit) : "—"} cls="text-success-fg" testId={`exp-pnl-${s}`} />
            <CardCell label="%Profit (N)" value={pct(row.pct_profit, 3)} testId={`pct-profit-${s}`} />
            <CardCell label="%/Day (O)" value={pct(row.pct_per_day, 4)} cls="text-success-fg" testId={`pct-day-${s}`} />
          </div>
        </CardSection>

        <CardSection title="Execution" testId={`sec-execution-${s}`}>
          <KV label="Levels entered" testId={`card-levels-${s}`}
            value={(() => {
              const rows = trade.capacity_plan?.levels;
              const reach = rows ? rows.filter((r) => (r.affordable_lots || 0) > 0).length : trade.tradable_levels;
              return `${trade.entered_levels ?? 0} / ${reach ?? "—"}`;
            })()} />
          <KV label="Pairs open" value={pairOn ? (trade.open_hedged_pairs ?? 0) : "— (level entry)"} testId={`card-pairs-${s}`} />
          <KV label="Partial pairs" value={trade.partial_pairs ?? 0} cls={(trade.partial_pairs || 0) > 0 ? "text-warning-fg" : "text-fg"} testId={`card-partial-${s}`} />
          <KV label="Entry state" testId={`card-entry-${s}`}
            value={trade.current_partial_level
              ? `ENTERING L${trade.current_partial_level}${entryBlock(trade)?.state === "STALLED" ? " · STALLED"
                : entryBlock(trade) ? ` · RETRY ${entryBlock(trade).retries_used + 1}/${entryBlock(trade).max_retries}` : ""}`
              : trade.entry_gate?.reason ? String(trade.entry_gate.reason).replace(/_/g, " ")
                : st === "RUNNING" ? "Watching" : "—"} />
          <KV label="Auto exit" testId={`card-auto-exit-${s}`}
            value={trade.auto_exit ? `${trade.auto_exit.state}${trade.auto_exit.sequence_no ? ` · seq ${trade.auto_exit.sequence_no}` : ""}`
              : trade.auto_square_off ? "Armed" : "Off"}
            cls={trade.auto_exit ? "text-warning-fg" : "text-fg"} />
          <KV label="Capital used" value={inr(trade.paper_capital_used ?? trade.funds_used)} testId={`card-capital-${s}`} />
        </CardSection>

        <CardSection title="Risk & State" testId={`sec-state-${s}`}>
          <div className="flex flex-col items-start gap-1">
            <span className="text-[12px] text-fg-secondary">Rollover</span>
            {rollNow
              ? <RolloverStatus trade={trade} offered={rollOffer} testId={`rollover-state-${s}`} />
              : <span className="text-[12px] text-fg-secondary">{rollShown ? rollReason : "Not applicable"}</span>}
          </div>
          <KV label="Reconciliation" testId={`card-recon-${s}`}
            value={trade.reconciliation_state == null ? "n/a (paper)" : trade.reconciliation_state}
            cls={trade.reconciliation_state && trade.reconciliation_state !== "RECONCILED" ? "text-danger-fg" : "text-fg"} />
          <KV label="Market data" testId={`card-data-${s}`} value={stale ? "STALE" : hasRow ? "OK" : "—"}
            cls={stale ? "text-danger-fg" : "text-fg"} />
          {trade.last_exit_check?.at && <KV label="Last engine check" value={shortTime(trade.last_exit_check.at)} />}
        </CardSection>
      </CardSections></div>
      </TradeDetailsModal>

      {/* ── E. Actions: run · position · admin · danger (right) ── */}
      <CardFooter testId={`actions-${s}`}
        analyse={<DisabledReasonButton onClick={() => setAnalyse(true)} title="Where this spread position can go: P&L at any closing spread, from the confirmed entry prices"
          data-testid={`analyse-btn-${s}`}><BarChart3 className="h-3 w-3" aria-hidden="true" /> {ANALYSE_LABEL}</DisabledReasonButton>}
        details={<><DetailsButton open={expanded} onClick={() => setExpanded(true)} testId={`details-btn-${s}`} /><DisabledReasonButton onClick={onOpenPlan} data-testid={`trade-plan-btn-${s}`} title="Ladder levels, spread triggers, funding, audit and change opportunity">
          <ListChecks className="h-3 w-3" aria-hidden="true" /> Plan &amp; ladder</DisabledReasonButton></>}
        danger={<>
          <DisabledReasonButton variant="danger" onClick={() => setConfirmSqOff(true)} disabled={!canSquareOff}
            reason={busy ? "Working…" : "No open exposure to square off"} data-testid={`sqoff-btn-${s}`}>
            <XCircle className="h-3.5 w-3.5" aria-hidden="true" /> {SQUARE_OFF_LABEL}</DisabledReasonButton>
          {onRemove && (
            <DisabledReasonButton variant="danger" onClick={onRemove} disabled={busy || openPos}
              reason={openPos ? "Square off the open position before removing" : "Working…"} title="Remove this trade"
              data-testid={`remove-btn-${s}`}><Trash2 className="h-3 w-3" aria-hidden="true" /> Remove</DisabledReasonButton>
          )}
        </>}>
        {/* One cycle per trade: Start only from READY/STOPPED. A COMPLETE trade is removed and re-created. */}
        <DisabledReasonButton variant="success" onClick={() => act("start")} disabled={!canStart} reason={startReason} data-testid={`start-btn-${s}`}>
          <Play className="h-3 w-3" aria-hidden="true" /> Start</DisabledReasonButton>
        <DisabledReasonButton onClick={() => act("stop")} disabled={!canStop} reason={busy ? "Working…" : "Not running"} data-testid={`stop-btn-${s}`}>
          <Square className="h-3 w-3" aria-hidden="true" /> Stop</DisabledReasonButton>
        <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
        <DisabledReasonButton variant={trade.auto_square_off ? "primary" : "secondary"} aria-pressed={!!trade.auto_square_off}
          onClick={() => act("auto-square-off", { auto_square_off: !trade.auto_square_off })} disabled={busy || st === "COMPLETE"}
          reason={st === "COMPLETE" ? "Trade is COMPLETE" : "Working…"} title="Toggle Auto Square-Off" data-testid={`auto-btn-${s}`}>
          Auto Sq {trade.auto_square_off ? "ON" : "OFF"}
        </DisabledReasonButton>
        {rollShown && (
          <DisabledReasonButton onClick={() => setRollOpen(true)} disabled={busy || !rollOffer} reason={busy ? "Working…" : rollReason}
            title="Roll the position onto the next contract pair" data-testid={`rollover-btn-${s}`}
            className={rollOffer && !busy ? "!bg-[#7c3aed] !text-white !border-transparent" : ""}>
            Roll Over{trade.earlier_leg_dte != null ? ` · ${trade.earlier_leg_dte}d` : ""}
          </DisabledReasonButton>
        )}
        {rolling && ro.state !== "ROLL_STOPPED" && (
          <DisabledReasonButton variant="warning" onClick={() => act("rollover/stop")} disabled={busy} data-testid={`rollover-stop-btn-${s}`}>Stop Rollover</DisabledReasonButton>
        )}
        {canResumeRoll && (
          <DisabledReasonButton onClick={() => act("rollover/resume", { confirm: true, environment: trade.environment })} disabled={busy}
            data-testid={`rollover-resume-btn-${s}`}>Resume Rollover</DisabledReasonButton>
        )}
        <span className="mx-1 h-5 w-px bg-line" aria-hidden="true" />
        <DisabledReasonButton onClick={() => act("reset")} disabled={!canReset}
          reason={st === "COMPLETE" ? "Trade is COMPLETE — remove it and create a new trade" : "Working…"}
          data-testid={`reset-btn-${s}`}><RotateCcw className="h-3 w-3" aria-hidden="true" /> Reset</DisabledReasonButton>
        {/* Audit and Change Opportunity live in Plan (Audit tab, and the Plan header). */}
      </CardFooter>
      {confirmSqOff && (
        <ConfirmSquareOffDialog tradeId={trade.id} tradeName={trade.name}
          onClose={() => setConfirmSqOff(false)} onDone={onChange} />
      )}
      <CardDialog open={analyse} onClose={() => setAnalyse(false)} wide testId={`analyse-dialog-${s}`}
        title={`Analyse Trade · ${trade.name}`} sub={`${trade.assigned_commodity || ""} · ${trade.near_symbol || ""} / ${trade.far_symbol || ""} · ${st}`}>
        {analyse && <FuturesSpreadAnalysis trade={trade} />}</CardDialog>
      {rollOpen && (
        <RolloverDialog tradeId={trade.id} tradeName={trade.name}
          onClose={() => setRollOpen(false)} onDone={onChange} />
      )}
    </CardFrame>
  );
}

/* Why trades are waiting, from the ONE status (lib/statusModel): not connected, markets closed, or data not live.
   It used to say "Zerodha not connected" whenever prices were not fresh - including after the bell, connected. */
function MarketWaitBanner({ onBroker }) {
  const { systemStatus } = useApp() || {};
  const w = marketWait(systemStatus);
  return (
    <div className="term-panel rounded-sm px-3 py-2 flex flex-wrap items-center gap-2 text-xs" data-testid="paper-waiting" data-tone={w?.tone || "loading"}>
      <AlertTriangle className={`h-4 w-4 shrink-0 ${w?.tone === "idle" ? "text-slate-400" : "text-[#FFCC00]"}`} />
      <span className="font-semibold text-slate-200" data-testid="paper-waiting-title">{w ? w.title : "Checking Zerodha and market data…"}</span>
      {w && <span className="text-slate-500">{w.sub}</span>}
      {w?.broker && <button onClick={onBroker} className="ml-auto gz-btn gz-btn-secondary">Broker Session</button>}
    </div>
  );
}

export default function PaperTrading() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [recov, setRecov] = useState(null);       // LIVE-11: trades parked by a restart
  const [recovBusy, setRecovBusy] = useState("");
  const [assignT, setAssignT] = useState(null);
  const [planT, setPlanT] = useState(null);
  const [settingsT, setSettingsT] = useState(null);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const openId = params.get("open");

  // ONE unified list across markets AND environments — different opportunity models and
  // different execution adapters, but the same engine, the same cards and the same controls.
  // The environment filter used to be left out, so the endpoint defaulted to PAPER and a LIVE
  // or SHADOW trade was invisible here: it could be created but never seen or started. Each
  // card carries its own environment badge, so they are never confusable.
  const load = useCallback(() => sharedGet("/paper/v25/trades", { params: { market: "ALL", environment: "ALL" } })
    .then((r) => { setData(r.data); setLoadError(null); })
    .catch(() => setLoadError("Trade updates are unavailable. Any displayed values are from the last successful update.")), []);
  useEffect(() => { load(); return visibleInterval(load, 3000); }, [load]);

  // LIVE-11. A LIVE trade that could be holding a position comes back from a restart PARKED:
  // it opens nothing until the broker has been asked what it really holds and a person has
  // agreed that trading it again is the right thing to do.
  const loadRecovery = useCallback(() => api.get("/recovery")
    .then((r) => setRecov(r.data)).catch(() => {}), []);
  useEffect(() => { loadRecovery(); return visibleInterval(loadRecovery, 5000); },
    [loadRecovery]);

  const runRecovery = async () => {
    setRecovBusy("run");
    try {
      await api.post("/recovery/run", {});
      await loadRecovery(); load();
      toast.success("Asked Zerodha what it holds");
    } catch (e) { toast.error(detailText(e, "Recovery failed")); }
    finally { setRecovBusy(""); }
  };

  const acknowledgeRecovery = async (row) => {
    const note = window.prompt(`Resume automation for ${row.name}? What did you check?`);
    if (note === null) return;
    setRecovBusy(row.id);
    try {
      await api.post(`/recovery/${row.id}/acknowledge`, { note: note.trim() || null });
      await loadRecovery(); load();
      toast.success(`${row.name}: automation resumed`);
    } catch (e) { toast.error(detailText(e, "Acknowledge failed")); }
    finally { setRecovBusy(""); }
  };


  const removeTrade = async (t) => {
    try { await api.delete(`/paper/v25/trades/${t.id}`); toast.success(`${t.name} removed`); load(); }
    catch (e) { toast.error(detailText(e, "Remove failed")); }
  };

  if (!data) return <div className="p-4"><RequestStatus error={loadError} loading="Loading trades…" onRetry={load} /></div>;
  const parity = data.parity || {};

  return (
    <div className="p-3 space-y-2">
      <RequestStatus error={loadError} onRetry={load} />
      {assignT && <AssignModal trade={assignT} onClose={() => setAssignT(null)} onAssigned={() => { setAssignT(null); load(); }} />}
      {planT && (
        <TradePlanModal trade={data.trades.find(t => t.id === planT.id) || planT} onClose={() => setPlanT(null)}
          onEditSettings={() => { setSettingsT(data.trades.find(t => t.id === planT.id) || planT); setPlanT(null); }}
          onAssign={() => { setAssignT(data.trades.find(t => t.id === planT.id) || planT); setPlanT(null); }} />
      )}
      {settingsT && (
        <TradeConfigModal
          trade={settingsT} mode="edit"
          onClose={() => setSettingsT(null)}
          onConfirm={async (cfg) => {
            try {
              await api.post(`/paper/v25/trades/${settingsT.id}/settings`, cfg);
              toast.success("Settings updated"); setSettingsT(null); load();
            } catch (e) { toast.error(detailText(e, "Update failed")); throw e; }
          }}
        />
      )}

      {/* LIVE-11. Nothing resumes by itself after a restart. This banner is the acknowledgement. */}
      {recov?.pending?.length > 0 && (
        <div className="rounded-sm border border-[#FFCC00] bg-[#FFCC00]/10 p-3 space-y-2"
          data-testid="recovery-banner">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="mono text-[12px] font-bold text-[#FFCC00]">
              ⚠ {recov.pending.length} LIVE trade{recov.pending.length > 1 ? "s" : ""} came back from a restart
            </span>
            <span className="text-[11px] text-slate-300">
              New entries are blocked for {recov.pending.length > 1 ? "them" : "it"}. Exits, hedging and monitoring continue.
            </span>
            <button onClick={runRecovery} disabled={!!recovBusy}
              data-testid="recovery-run-btn"
              className="ml-auto px-3 py-1.5 rounded-sm text-xs font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730]">
              {recovBusy === "run" ? "ASKING…" : "ASK ZERODHA WHAT IT HOLDS"}
            </button>
          </div>
          {recov.pending.map((row) => (
            <div key={row.id} className="term-panel-2 rounded-sm px-3 py-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="mono text-[11px] text-slate-200 font-bold">
                  {row.name} · {row.recovery_state}
                </div>
                <div className="mono text-[10px] text-slate-400 truncate">
                  {(row.recovery_reasons || []).join(" · ") || "—"}
                </div>
                {row.recovery_detail?.blockers?.length > 0 && (
                  <div className="mono text-[10px] text-[#FF6b62] truncate">
                    needs a human: {row.recovery_detail.blockers.join(", ")}
                  </div>
                )}
                {row.recovery_detail?.adopted && (
                  <div className="mono text-[10px] text-slate-500 truncate">
                    adopted {row.recovery_detail.adopted.found?.length || 0} · released{" "}
                    {row.recovery_detail.adopted.released?.length || 0} · unresolved{" "}
                    {row.recovery_detail.adopted.unanswerable?.length || 0}
                  </div>
                )}
              </div>
              <button onClick={() => acknowledgeRecovery(row)}
                disabled={!!recovBusy || row.recovery_state === "RECOVERY_PENDING"}
                title={row.recovery_state === "RECOVERY_PENDING"
                  ? "Ask Zerodha first — nothing has been confirmed yet"
                  : "Resume automation for this trade"}
                data-testid={`recovery-ack-${row.id}`}
                className="px-2 py-1 rounded-sm text-[10px] font-semibold bg-[#1A1D24] border border-[#2A2F3A] hover:bg-[#232730] disabled:opacity-40">
                ACKNOWLEDGE &amp; RESUME
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <h1 className="text-lg font-bold tracking-tight flex items-center gap-2 whitespace-nowrap"><Zap className="h-5 w-5 text-[#00C805]" /> Futures Trading</h1>
          {(() => {
            // What this list actually holds right now, rather than a fixed "PAPER" claim.
            const envs = [...new Set((data.trades || []).map((t) => t.environment || "PAPER"))];
            const live = envs.includes("LIVE"), shadow = envs.includes("SHADOW");
            return (
              <span data-testid="page-environment-badge"
                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${live ? "bg-[#FF3B30]/15 text-[#FF6b62]" : shadow ? "bg-[#FFCC00]/15 text-[#FFCC00]" : "bg-[#00C805]/15 text-[#00C805]"}`}>
                {live ? (envs.length > 1 ? "LIVE + SIMULATED · ZERODHA DATA" : "LIVE · ZERODHA DATA")
                  : shadow ? "SIMULATED (shadow + paper) · ZERODHA DATA" : "SIMULATED (paper) · ZERODHA DATA"}
              </span>
            );
          })()}
          <span data-testid="parity-banner" title={parity.passed ? `Analysier ${parity.checked_analysier} · Dashboard ${parity.checked_dashboard} checks` : JSON.stringify(parity.failures)}
            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm inline-flex items-center gap-1 ${parity.passed ? "bg-[#00C805]/10 text-[#00C805]" : "bg-[#FF3B30]/15 text-[#FF6b62]"}`}>
            {parity.passed ? <ShieldCheck className="h-3 w-3" /> : <ShieldAlert className="h-3 w-3" />}
            {parity.passed ? "PARITY PASS" : "PAPER_BLOCKED_PARITY_FAILURE"}
          </span>
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${data.live_trading_enabled ? "bg-[#FF6b62]/15 text-[#FF6b62]" : "bg-[#475569]/25 text-slate-400"}`}>LIVE {data.live_trading_enabled ? "ENABLED" : "DISABLED"}</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center flex-wrap gap-2 text-[11px] mono" data-testid="trade-count">
            <span className="text-slate-400">{data.trades.length} trade{data.trades.length === 1 ? "" : "s"}</span>
            {["RUNNING", "EXITING", "READY", "STOPPED", "COMPLETE"].map((k) => {
              const n = data.trades.filter((t) => t.status === k).length;
              return n ? <span key={k} data-testid={`status-count-${k.toLowerCase()}`}
                className={`px-1.5 py-0.5 rounded-sm font-bold ${STATUS_PILL[k].cls}`}>{n} {k}</span> : null;
            })}
            <span className="text-slate-500">{data.trades.filter((t) => t.market_segment === "MCX").length} MCX · {data.trades.filter((t) => t.market_segment === "NFO").length} NFO</span>
          </span>
          <button onClick={() => nav("/opportunities")} className="gz-btn gz-btn-primary" data-testid="add-trade-btn"><Plus className="h-3 w-3" /> Add Trade</button>
        </div>
      </div>
      {!parity.passed && <div className="text-[11px] text-[#FF6b62] mono break-all">{JSON.stringify(parity.failures)}</div>}

      {!data.zerodha_ready && <MarketWaitBanner onBroker={() => nav("/broker")} />}

      <div className="space-y-2">
        {data.trades.length === 0 && (
          <div className="term-panel rounded-sm p-6 text-center text-xs text-slate-500" data-testid="paper-empty">
            No trades yet — choose an opportunity to configure your first trade. <button onClick={() => nav("/opportunities")} className="btn-x text-[#5b9bff] underline">Browse Opportunities</button>
          </div>
        )}
        {data.trades.map((t) => (
          <TradeCard key={t.id} trade={t} onChange={load}
            onOpenPlan={() => setPlanT(t)} onEditSettings={() => setSettingsT(t)}
            onRemove={() => removeTrade(t)} forceOpen={openId === t.id}
            duplicateName={data.trades.filter((o) => o.name === t.name).length > 1} />
        ))}
      </div>

      <div className="text-[11px] text-slate-500">Each trade shows its execution environment. PAPER uses simulated fills; SHADOW LIVE sends broker-validation orders; LIVE sends real orders. Strategy capital and broker execution checks are reported separately.</div>
    </div>
  );
}
